// The web app's words in English: the catalogue every other language matches, key for key. Keys are named
// for where they show (the shell, a page) and what they say. Words that take values are functions.
import type { Decision, OnboardingQuestion } from "../api.js";

/** Words with a capital first letter, for a sentence that starts with them. */
const upperFirst = (words: string) => `${words[0]?.toUpperCase() ?? ""}${words.slice(1)}`;
/** Words with a small first letter, for a sentence they stand inside: "for sales order SO/26-27/0010". */
const lowerFirst = (words: string) => `${words[0]?.toLowerCase() ?? ""}${words.slice(1)}`;

/** Why a Run stops, as the api records it (#570): the parameters of each stop code. */
export interface StopParameters {
  readonly no_committed_date: { readonly order: string };
  readonly no_bill_of_materials: { readonly order: string; readonly items: readonly string[] };
  /** `due`: the order's committed date, YYYY-MM-DD. */
  readonly material_short: { readonly order: string; readonly due: string; readonly lines: readonly ShortLine[] };
  /** `stale`: a rate on the Item that is not a GST rate now. */
  readonly no_gst_rate: {
    readonly item: string;
    readonly hsn: string;
    readonly invoice?: string | undefined;
    readonly stale?: number | undefined;
  };
  /** `invoices`: the numbers of the earlier invoices that billed the order in full. */
  readonly already_billed: { readonly order: string; readonly invoices: readonly string[] };
}

/** One short Item of a material check, in its stock unit, with the requisition raised for it. */
export interface ShortLine {
  readonly name: string;
  readonly unit: string;
  readonly needs: number;
  readonly on_hand: number;
  readonly short: number;
  readonly requisition?:
    { readonly number: string; readonly quantity: number; readonly supplier?: string | undefined } | undefined;
}

/** A quantity as the stores count it, in its stock unit: up to three decimals, no trailing zeros. */
export const quantityIn = (quantity: number, unit: string) =>
  `${String(Math.round(quantity * 1000) / 1000)}${unit ? ` ${unit}` : ""}`;

/** A date (YYYY-MM-DD) as the business reads it in `locale`: 30 Sept 2026 in English. */
export const dayIn = (locale: string, iso: string) =>
  new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(
    new Date(`${iso}T00:00:00Z`),
  );

const shortLine = (l: ShortLine) => {
  const pr = l.requisition;
  const raised = pr
    ? `; purchase requisition ${pr.number} raised for ${quantityIn(pr.quantity, l.unit)}${pr.supplier ? ` to ${pr.supplier}` : ""}`
    : "";
  return (
    `${l.name}: needs ${quantityIn(l.needs, l.unit)}, ${quantityIn(l.on_hand, l.unit)} on hand, ` +
    `short of ${quantityIn(l.short, l.unit)}${raised}`
  );
};

/** A Set up LAMP question as the api asks it, in English: it names the GSTIN itself. */
const promptAsAsked: (question: OnboardingQuestion, gstin: string | undefined) => string = (question) =>
  question.prompt;

/** What the Audit Log records that is not a Run's own event, as a sentence, by the event's name (#462). */
const AUDIT_ACTIONS: Readonly<Record<string, string>> = {
  TreeChangeApplied: "Changed how the business works",
  FileReceived: "Received a file",
  FileKindProposed: "Read what kind of paper a file is",
  FileKindConfirmed: "Confirmed what kind of paper a file is",
  FileNotRead: "Could not read a file",
  SupplierParticularsChanged: "Changed the business's particulars on its invoices",
  LanguageChanged: "Changed the language LAMP speaks to them",
  LicenceStateChanged: "The Cell's licence changed",
  ReadOnlyReasonSet: "Put the business in read-only mode",
  ReadOnlyReasonCleared: "Took the business out of read-only mode",
  ReleaseSet: "Moved the business to a new LAMP release",
  ReleaseNotesSent: "Told the Owners what changed in LAMP",
  ReleaseAlarmFired: "Stopped a LAMP release that went wrong",
  RingChanged: "Changed whether the business gets LAMP's changes early",
  RolloutRolledBack: "Rolled back a LAMP release",
  ContentReleaseImported: "Took in a new set of LAMP's content",
  ContentReleaseRefused: "Refused a set of LAMP's content",
  ContentReleaseRolledBack: "Went back to the previous set of LAMP's content",
  ExternalAssistantGranted: "Let an outside assistant connect",
  ExternalAssistantRevoked: "Disconnected an outside assistant",
  ExternalAssistantsRevokedForTenant: "Disconnected every outside assistant",
  ExternalAssistantRefreshTokenReused: "Cut off an outside assistant that reused its sign-in",
  ExternalAssistantToolCalled: "An outside assistant worked in LAMP",
  BreakGlassOpened: "LAMP support opened access to the business",
  BreakGlassUsed: "LAMP support used its access to the business",
  BreakGlassClosed: "LAMP support's access to the business closed",
  SignedOutEverywhere: "Signed out everywhere else",
  AutonomyLowered: "Lowered how much LAMP does on its own",
  KillSwitchEngaged: "Paused LAMP's agents",
  KillSwitchReleased: "Resumed LAMP's agents",
  ConsentGiven: "Gave their consent",
  ConsentWithdrawn: "Withdrew their consent",
  RightsRequestReceived: "Asked about their data (a privacy request)",
  RightsRequestVerified: "Confirmed a privacy request with its code",
  RightsRequestCompleted: "Answered a privacy request",
  RightsRequestRefused: "Turned down a privacy request, saying why",
  BusinessDataExported: "Downloaded all of the business's data",
  OffboardingStarted: "Started closing the business's LAMP account",
  OwnerConfirmed: "Confirmed what closing the account does",
  ArchiveChosen: "Chose whether LAMP keeps an archive",
  ExportTaken: "Took the business's data before closing",
  OffboardingCancelled: "Stopped closing the business's LAMP account",
  TenantShredded: "Closed the business's LAMP account for good",
  ScheduledRunSkipped: "Skipped a Run that was due",
  SetupProblemRaised: "Found a problem in how the business is set up",
  Talk: "Talked with LAMP",
};
const openRuns = (n: number) => `${n} open Run${n === 1 ? "" : "s"}`;
const count = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

type AmountLabel = NonNullable<Decision["amount"]>["label"];

/** A Business Profile value onboarding sets, in plain words, by profile key and value. */
const PROFILE_WORDS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  manufacturing_mode: {
    machining: "machining parts to customer drawings",
    fabrication: "sheet-metal or structural fabrication to order",
    assembly: "assembling machines or panels to order",
    moulding: "moulding to order",
    casting: "casting or forging to order",
    "batch-process": "making in batches",
  },
  gst_registration: {
    regular: "registered for GST",
    composition: "registered for GST under the composition scheme",
    unregistered: "not registered for GST",
  },
  size_band: { micro: "a micro enterprise", small: "a small enterprise", medium: "a medium enterprise" },
  headcount_band: { "<10": "fewer than 10 people", "10–19": "10 to 19 people", "20+": "20 people or more" },
  software: {
    "named platform": "accounts kept in accounting or ERP software",
    spreadsheets: "accounts kept in spreadsheets",
    none: "accounts kept on paper",
  },
};

export const en = {
  // The app as a whole.
  loading: "Loading…",
  languageSwitch: "हिंदी / English",

  // The pages, as the sidebar, the top bar and the tab bar name them.
  pageToday: "Today",
  pageChat: "Chat with LAMP",
  pageFloor: "Floor",
  pageRuns: "Runs",
  pageDocuments: "Documents",
  pageParties: "Customers and suppliers",
  pageItems: "Items and stock",
  pageCash: "Cash and ledgers",
  pageGst: "GST and compliance",
  pageTree: "Business Tree",
  pageRules: "Rules and Playbooks",
  pageTemplates: "Templates",
  pageSchedules: "Schedules",
  pageMemory: "Memory",
  pageQuality: "Quality",
  pageInsights: "Insights",
  pageBills: "Expense Bills",
  pageExpenses: "Expenses",
  pageCredits: "Credits and usage",
  pageAudit: "Audit log",
  pageOnboarding: "Set up LAMP",
  pageUsers: "Users",
  pageRoles: "Roles and permissions",
  pageChannels: "Channels",
  pageConnections: "Connections",
  pageModels: "AI models",
  pageSettings: "Settings",
  pageBilling: "Plan and billing",
  pageSecurity: "Security",
  pageHelp: "Help",
  pageStatus: "System status",
  pageRefused: (page: string) => `Your Roles do not open ${page}, so here is Today.`,
  navRun: "Run the business",
  navMoney: "Money and compliance",
  navHow: "How LAMP works for you",
  navWatch: "Watch",
  navSetup: "Setup",
  tabToday: "Today",
  tabFloor: "Floor",
  tabChat: "Chat",
  tabDocuments: "Documents",

  // The shell.
  shellTitle: (page: string) => `${page} · LAMP`,
  shellSkip: "Skip to content",
  shellBusiness: "Your business",
  shellSwitchBusiness: "Switch business",
  shellOtherBusiness: "Sign in to another business",
  shellCredits: "Credits this month",
  shellTrialLeft: (days: number) => (days === 1 ? "Trial: last day" : `Trial: ${days} days left`),
  shellSearch: "Search",
  shellSearchHint: "Search orders, customers, documents",
  shellSearching: "Searching…",
  shellSearchResults: "Search results",
  shellNoMatches: "Nothing matches.",
  shellSearchFailed: "Search failed. Try again shortly.",
  shellOrder: "Order",
  shellCounterparty: "Customer or supplier",
  shellDocument: "Document",
  shellNotifications: "Notifications",
  shellEmailMe: "Email me",
  shellEmailNewWork: "When new work waits on me",
  shellEmailDigest: "Each morning, what waits on me and what is late",
  shellEmailAddress: "At this email",
  shellEmailSave: "Save",
  shellEmailSaved: "Saved.",
  shellEmailNeedsAddress: "Give the email to send them to, then save.",
  shellEmailCodeSent: (email: string) =>
    `We emailed a code to ${email}. Enter it here: nothing about your work goes there until you do.`,
  shellEmailCode: "Code from the email",
  shellEmailConfirm: "Confirm",
  shellEmailConfirmed: "Confirmed: the emails go to this address.",
  shellEmailWrongCode:
    "That code did not work. Check it: once it has expired or been tried too often, save again for a new one.",
  shellEmailSaveForCode: "No code is waiting for this address: save to get one.",
  shellEmailCodeWaiting: (email: string) =>
    `Saved. Enter the code already emailed to ${email}: saving again sends no new one while it works.`,
  shellEmailCodeNotSent: "Saved, but no code was emailed this time. Save again later to get one.",
  shellEmailCodeHeld:
    "Saved. You have had as many codes as LAMP sends in an hour: save again in an hour and the code comes then.",
  shellNothingNew: "Nothing new.",
  shellNotificationsCount: (count: number) => `Notifications: ${count} new`,
  shellNewWork: "New for you",
  shellLateWork: "Late",
  shellNewMessages: "Messages",
  shellAccount: "Account",
  shellOtherLanguage: "हिंदी में देखें",
  shellPreferences: "Your preferences",
  shellSignOut: "Sign out",
  shellMenu: "Menu",
  shellClose: "Close",
  shellTabs: "Tabs",
  shellSoon: "This page isn't built yet. Ask LAMP in Chat meanwhile.",
  shellOpenChat: "Open Chat with LAMP",
  shellSettingsTabs: "Settings",
  shellBusinessSettings: "Business settings",
  shellPrivacy: "Privacy",

  // Signing in.
  signInTitle: "Sign in to LAMP",
  signInEmail: "Email",
  signInEmailMe: "Email me a code",
  signInSent: (email: string) => `We sent a code to ${email}. It works for 10 minutes.`,
  signInCode: "Code",
  signInTrust: "Keep me signed in on this device",
  signInSubmit: "Sign in",
  signInOtherEmail: "Use another email",
  signInTooMany: "Too many codes asked for. Wait a few minutes and try again.",
  signInCheck: "Check the email, then try again.",
  signInChoose: "Which business?",
  signInChooseIntro: "Your email is in more than one business on LAMP. Choose the one to sign in to.",
  invoiceOpening: "Opening the invoice…",
  invoiceGone: "This link has expired or is not one we sent. Ask the business that sent you the invoice for it again.",
  invoiceTitle: (number: string, business: string) => `Invoice ${number} from ${business}`,
  invoiceFor: (customer: string, date: string) => `For ${customer}, dated ${date}.`,
  invoicePrinting: "Its PDF is being printed. Open this link again in a few minutes.",
  quotationOpening: "Opening the quotation…",
  quotationGone:
    "This link has expired or is not one we sent. Ask the business that sent you the quotation for it again.",
  quotationUnreachable: "We couldn't reach the business just now. Try again in a moment.",
  quotationTitle: (number: string, business: string) => `Quotation ${number} from ${business}`,
  quotationFor: (customer: string, date: string, validUntil: string) =>
    `For ${customer}, dated ${date}, valid until ${validUntil}.`,
  quotationItem: "Item",
  quotationQuantity: "Quantity",
  quotationRate: "Rate",
  quotationAmount: "Amount",
  quotationTotal: (total: string) => `Total ${total}, GST extra at the rate in force on the invoice date.`,
  quotationPdf: "Download the PDF",
  quotationAccept: "Accept",
  quotationAsk: "Ask a question",
  quotationDecline: "Decline",
  quotationQuestion: "Your question",
  quotationSend: "Send",
  quotationBack: "Back",
  quotationAnswered: (answer: "accept" | "question" | "decline", business: string) =>
    answer === "accept"
      ? `Thank you. ${business} has your acceptance and will confirm the order with you.`
      : answer === "decline"
        ? `Thank you for letting ${business} know.`
        : `Thank you. ${business} has your question and will answer you.`,
  invitationTitle: "Your invitation to LAMP",
  invitationOpening: "Opening your invitation…",
  invitationFor: (business: string | null, roles: readonly string[]) =>
    `${business ?? "Your business"} invited you to LAMP${roles.length > 0 ? ` as ${roles.join(", ")}` : ""}.`,
  invitationAccept: "Accept and sign in",
  invitationGone:
    "This invitation link has already been used, has expired or was replaced by a newer one. Ask whoever invited you to send it again, or sign in with your email.",
  invitationTelegram: "This invitation is for Telegram. Open its link on the phone with the invited number.",
  invitationSignIn: "Sign in with your email",
  invitationPin: "A PIN, for when a code does not arrive (optional)",
  invitationWeakPin: "That PIN is too easy to guess. Choose six digits that are not a pattern, and try again.",
  invitationPinSixDigits: "A PIN is six digits. Enter six, or leave it empty.",
  invitationTooMany: "Too many tries. Wait a few minutes and accept again.",
  invitationNoPin: "Accept without a PIN",
  // The consent a PIN is kept with (privacy notice section 6, #698).
  pinConsentHeading: "May LAMP keep your PIN?",
  pinConsentMessage:
    "Your PIN is sensitive data. LAMP keeps it only with your consent, stored as a one-way hash it cannot read back, to confirm it is you when a code does not arrive. You can withdraw this consent in Settings → Privacy, which deletes the PIN.",
  pinConsentNotice: "Read the Privacy Notice",
  pinConsentConfirm: "I agree, keep my PIN",
  signInNotKept: "Signed in, but the session was not kept. Check that cookies are allowed.",
  signInBadCode: "That code didn't work. Check it, or ask for a new one.",

  // Today.
  todayLoading: "Loading Today…",
  todayFailed: "Today could not be loaded. Try again shortly.",
  todayRefused: "LAMP refused it.",
  todayNothing: "Nothing needs you right now.",
  todayCount: (count: number) => (count === 1 ? "1 decision for you." : `${count} decisions for you.`),
  todayBiggest: (amount: string, label: AmountLabel, who: string | undefined) =>
    `The biggest is ${amount} ${label}${who === undefined ? "" : ` ${label === "to bill" ? "to" : "from"} ${who}`}.`,
  todayQueue: "Decisions for you",
  todayMarkDone: "Mark as done",
  todayReview: "Review and confirm",
  todaySeeWrong: "See what's wrong",
  todayMore: "More about this",
  todayCodes: "Reference codes",
  todayOpenRun: "Open the run",
  todayOpenBreakdown: "Open the cost breakdown",
  todayNoteMargin: "Note it and carry on",
  todayMarginNote: "Your note (optional)",
  todayStartRateRevision: "Start the rate revision with the customer",
  todayCall: (name: string) => `Call ${name}`,
  todayNoticed: "LAMP noticed",
  /** The person's Reminders whose day has come (#510), and one a colleague set for them. */
  todayReminders: "Reminders",
  todayReminderFrom: (text: string, from: string) => `${text} (from ${from})`,
  todayIssued: "Recently issued",
  todayNoIssued: "No Documents issued yet.",
  todayIssuedTag: "Issued",
  todayInvoice: "Invoice",
  todayPdf: "(PDF)",
  todayAmountOverdue: "overdue",
  todayAmountToBill: "to bill",
  todayAmountOrderValue: "order value",

  // Chat with LAMP.
  chatThread: "Conversation with LAMP",
  chatLoading: "Loading the conversation…",
  chatEmpty:
    "Type an order below, for example “10 hex bolts for Shree Motors at ₹12.50”. LAMP drafts it for you to check.",
  chatYou: "You",
  chatLamp: "LAMP",
  chatMessage: "Message LAMP",
  chatPlaceholder: "Type an order or a question",
  chatSend: "Send",
  chatSending: "Sending…",
  chatOwnerOnly: "Only an Owner pauses or resumes LAMP's agents.",
  chatAskOwner: "Ask an Owner of the business to do it.",
  chatWorking: (held: number) =>
    `LAMP's agents are working again.${held > 0 ? ` ${held} held messages are being sent.` : ""}`,
  chatChips: "Quick replies",
  /** A link to a Cockpit page a message names (#1016). */
  chatOpenPage: (page: string) => `Open ${page}`,
  chatAddThem: "Add them and draft the order",
  chatAddThemText: "Add them",
  chatChipOrder: "New order",
  chatChipOrderText: "10 hex bolts for Shree Motors at ₹12.50",
  chatChipPrivacy: "Privacy",

  // Files handed to LAMP in chat (Received Files).
  chatAttach: "Attach a file",
  chatCamera: "Take a photo",
  chatAttached: "Files to send",
  chatRemoveFile: (name: string) => `Remove ${name}`,
  chatDropHint: "Drop files here to send them to LAMP",
  chatFileType: (name: string) =>
    `${name} can't be sent: LAMP takes PDF, JPEG, PNG, XLSX and CSV files, and a Tally export's XML.`,
  chatFileSize: (name: string, megabytes: number) => `${name} is larger than ${megabytes} MB, so it can't be sent.`,
  chatFileEmpty: (name: string) => `${name} is empty.`,
  chatFileRecovery: "Choose another file, or send a photo of it.",
  chatOpenFile: (name: string) => `Open ${name}`,
  fileKind: (kind: string) =>
    ({
      customer_po: "A customer's purchase order",
      supplier_bill: "A supplier's bill",
      sales_invoice: "Our past sales invoice",
      stock_register: "A stock sheet",
      drawing: "A drawing",
      joining_form: "A joining form",
      attendance_register: "An attendance register",
      gate_pass: "A gate entry",
      delivery_challan: "A supplier's delivery challan",
      job_work_return: "A job worker's return challan",
      stock_count: "A stock count sheet",
      bank_statement: "A bank statement",
      complaint_note: "A customer's complaint note",
    })[kind] ?? kind,
  fileCard: (name: string) => `File ${name}`,
  fileFrom: (party: string) => `From or for: ${party}`,
  fileYes: "Yes",
  fileSomethingElse: "It's something else",
  fileWhatIsIt: "What is it?",
  fileNone: "None of these",
  fileRetry: "Try again",
  fileRecord: "Record it",
  fileRead: "What LAMP read",
  fileField: (key: string) =>
    ({
      supplierName: "Supplier",
      supplierGstin: "Supplier's GSTIN",
      billNumber: "Bill number",
      billDate: "Bill date",
      buyerName: "Customer",
      buyerGstin: "Customer's GSTIN",
      invoiceNumber: "Invoice number",
      invoiceDate: "Invoice date",
      taxableValuePaise: "Taxable value",
      gstPaise: "GST",
      totalPaise: "Total",
      drawingNumber: "Drawing number",
      title: "Title",
      revision: "Revision",
      material: "Material",
      itemName: "Item",
      quantity: "Quantity",
      unit: "Unit",
      unitPricePaise: "Rate",
      date: "Date",
      quantityIn: "In",
      quantityOut: "Out",
      learningNumber: "Document Learning",
      name: "Name",
      kind: "Customer or supplier",
      gstin: "GSTIN",
      salePricePaise: "Rate",
      customer: "Customer",
      gateEntryNumber: "Gate entry number",
      partyName: "From",
      vehicleNumber: "Vehicle",
      documentNumber: "Paper that came with it",
      orderReference: "Order written on it",
      challanNumber: "Challan number",
      challanDate: "Challan date",
      poNumber: "Our PO number",
      valuePaise: "Value",
      jobWorkerName: "Job worker",
      ourChallanNumber: "Our challan number",
      against: "Against",
      check: "To check",
      open: "Still open",
      sent: "Sent",
      withJobWorker: "With the job worker",
      returned: "Returned",
      rejected: "Rejected",
      scrap: "Scrap",
      withJobWorkerAfter: "With the job worker after",
      location: "Location",
      counted: "Counted",
      book: "Book stock",
      difference: "Difference",
      account: "Account",
      periodFrom: "From",
      periodTo: "To",
      openingBalancePaise: "Opening balance",
      closingBalancePaise: "Closing balance",
      matched: "Lines matched",
      unmatched: "Lines matching nothing",
      line: "Line",
      description: "Narration",
      reference: "Reference",
      creditPaise: "Money in",
      debitPaise: "Money out",
      match: "Matched to",
      party: "Customer or supplier",
      settles: "Settles",
      reason: "Why not",
    })[key] ?? key,
  fileCheckDrawing:
    "Check the number, revision and customer as LAMP read them; correct them if needed, then record it.",
  fileDevelop: (drawing: string, customer: string) => `Develop part ${drawing} for ${customer}`,
  fileMatch: (match: string) =>
    ({
      receipt: "A receipt already recorded",
      invoices: "Open invoices",
      payment: "A payment already recorded",
      bill: "An open bill",
    })[match] ?? match,
  fileKeep: "Keep it against…",
  fileKeepAgainst: "Keep it against",
  fileKeepObject: (object: string) =>
    ({
      customer: "A customer",
      supplier: "A supplier",
      item: "An Item",
      sales_order: "A sales order",
      purchase_order: "A purchase order",
      run: "A Run",
    })[object] ?? object,
  fileKeepName: "Name or number",
  fileKeepWords: "A line about it",
  fileKeepSubmit: "Keep it",
  fileKept: (what: string, label: string, words: string) => `Kept against ${what} ${label}: “${words}”`,
  fileReason: "Why",
  fileRecordAnyway: "Record it anyway",
  fileSendBack: "Send it back",
  fileSentBack: (reason: string) => `Sent back: ${reason}`,

  // A Tally export or a sheet of masters as data to bring in (#503): the Data Import's preview, confirmed once.
  fileDataImport: "Your data to bring in",
  fileImportConfirm: "Confirm and bring it in",
  fileImportPreview: (number: string, asOf: string) => `Data Import ${number}, at ${asOf}`,
  fileImportCustomers: "Customers",
  fileImportSuppliers: "Suppliers",
  fileImportJobWorkers: "Job workers",
  fileImportItems: "Items",
  fileImportNew: (n: number) => `${n} new`,
  fileImportBalances: "Opening balances",
  fileImportOwedToYou: "Owed to you",
  fileImportYouOwe: "You owe",
  fileImportStock: "Opening stock",
  fileImportPans: "PANs, kept sealed once you confirm",
  fileImportLinked: "Already in LAMP, linked",
  fileImportItem: "Item",
  fileImportQuantity: "Quantity",
  fileImportUnit: "Unit",
  fileImportAlready: (n: number) => `${n} already in LAMP, left out so nothing is added twice`,
  fileImportNotBrought: "Not brought in",
  fileImportWhat: "Where",
  fileImportName: "Name",
  fileImportWhy: "Why",
  fileImportDone: "Brought in",

  // A drafted BOM's or routing's Form on its card (4.2.1.1, 4.2.2.1).
  setUpFrom: (drawing: string, revision: string) => `Drafted from drawing ${drawing} revision ${revision}`,
  setUpUnmatched: "LAMP could not match these",
  setUpUnmatchedHelp: "Nothing on record does them. Add them, or arrange them outside LAMP.",
  setUpPart: "Part",
  setUpNewPart: "New part's name",
  setUpPartUnit: "Counted in",
  setUpMaterials: "Materials for one piece",
  setUpLine: (n: number) => `Line ${n}`,
  setUpItem: "Item",
  setUpQuantity: "Quantity",
  setUpUnit: "Unit",
  setUpScrap: "Scrap %",
  setUpAddLine: "Add a line",
  setUpRemoveLine: (n: number) => `Remove line ${n}`,
  setUpOperations: "Operations",
  setUpOperation: (n: number) => `Operation ${n}`,
  setUpSequence: "Sequence",
  setUpName: "Operation",
  setUpWorkCentre: "Work centre",
  setUpSetupMinutes: "Setup minutes",
  setUpCycleMinutes: "Minutes a piece",
  setUpAddOperation: "Add an operation",
  setUpRemoveOperation: (n: number) => `Remove operation ${n}`,
  setUpChoose: "Choose…",
  setUpJobWork: "Sent to a job worker",

  // A card in the thread.
  cardNeedsYou: "Needs you",
  cardYourTask: "Your Task",
  cardBlocked: "Blocked",
  cardWorking: "LAMP is on it",
  cardDone: "Done",
  /** A file LAMP could not take further: what went wrong is said in the thread. */
  fileNotDone: "Not done",
  cardPicked: "Picked and issued from stock",
  cardHandedOver: "Handed over with the goods",
  cardDoneIt: "Done it",
  cardReceivedBy: "Who received the goods and documents",
  cardHandedOverOn: "Handed over on",
  cardTransportDocument: "Transport document (LR or docket number), if any",
  cardEwayBillNumber: "E-way bill number the portal gave (12 digits)",
  cardEwayBillValidUntil: "Valid until",
  cardCommittedDate: "Delivery date promised to the customer",
  cardGstRate: "GST rate (%), such as 18",
  cardSendBack: "Send back",
  cardSeePrint: "See it as it will print",
  cardOpenPdf: (number: string) => `Open the PDF of ${number}`,
  cardConfirm: "Confirm",
  cardWhatChange: "What should change? (optional)",
  cardReleaseWhy: "Why release it for this order?",
  cardOverdue: "Overdue invoices",
  cardFiles: "The paper",
  cardOpenFile: (name: string) => `Open ${name}`,
  cardEmailTo: (name: string) => `Email to ${name}, once you confirm:`,
  cardEmailNoAddress: (name: string) => `${name} has no email on record: add one before you confirm.`,
  cardRelease: "Release this order",
  cardCancel: "Cancel",
  cardAddRow: "Add a line",
  cardRemoveRow: (n: number) => `Remove line ${String(n)}`,
  cardChoose: "Choose",
  cardFindOption: (label: string) => `Type to find ${label}`,
  cardLeftToAllocate: (amount: string) => `${amount} left to allocate`,
  cardNotAnAmount: (line: string) => `${line} is not an amount: say it as 30,000 or 4999.50`,
  cardOverAllocated: (amount: string) => `${amount} more than the total: take it off a line`,
  cardFileKind: "Only PDF, JPEG or PNG",
  cardFileEmpty: "This file is empty",
  cardFileTooLarge: "This file is over 5 MB",
  cardPrintTitle: (number: string) => `Invoice ${number} as it will print`,
  cardInvoice: (number: string) => `Invoice ${number}`,
  // lists/List.tsx
  listSearch: "Search",
  listLoading: (what: string) => `Loading ${what}…`,
  listForbidden: (what: string) => `Your Roles do not let you see ${what}.`,
  listFailed: (what: string) => `${upperFirst(what)} could not be loaded. Try again shortly.`,
  listPages: (what: string) => `Pages of ${what}`,
  listPrevious: "Previous",
  listNext: "Next",
  listPageOf: (page: number, pages: number, total: number, what: string) =>
    `Page ${page} of ${pages} · ${total} ${what}`,

  // documents/Documents.tsx
  documentsTitle: "Documents",
  documentsIntro: "Every invoice LAMP issued. Issued documents never change.",
  documentsFrom: "From",
  documentsTo: "To",
  documentsSearch: "Search documents",
  documentsWhat: "Documents",
  documentsNoMatch: "No Document matches.",
  documentsNone: "LAMP hasn't issued a Document yet.",
  documentsDocument: "Document",
  documentsCustomer: "Customer",
  documentsDate: "Date",
  documentsAmount: "Amount",
  documentsTaxInvoice: "Tax invoice",
  documentsTallyTitle: "Tally export",
  documentsTallyIntro:
    "A month's accounting entries as Tally vouchers, for your CA to import in Tally with Import > Vouchers. For the Owner and the Accountant.",
  documentsTallyMonth: "Month",
  documentsTallyDownload: (month: string) => `Download the Tally export for ${month}`,
  booksLinkTitle: "Tally vouchers",
  booksLinkInLamp: "Your statutory books are kept in LAMP.",
  booksLinkKeepInTally: "Keep the books in Tally",
  booksLinkKeepInLamp: "Keep the books in LAMP",
  booksLinkIntro: (since: string) =>
    `Your statutory books are kept in Tally. Every posting since ${since} goes there as one voucher, in a file you import in Tally with Import > Vouchers.`,
  booksLinkUnsent: (count: number) =>
    count === 0
      ? "Every voucher has been sent."
      : `${count} ${count === 1 ? "voucher is" : "vouchers are"} not yet sent.`,
  booksLinkMake: "Make the file of vouchers not yet sent",
  booksLinkNothing: "There are no vouchers to send.",
  booksLinkMade: (name: string, count: number) => `${name}: ${count} ${count === 1 ? "voucher" : "vouchers"}.`,
  booksLinkHeld: "Not in the file, until what is missing is mapped in Settings → Ledger Mapping:",
  booksLinkFile: "File",
  booksLinkVouchers: "Vouchers",
  booksLinkMadeAt: "Made",
  booksLinkAgain: "Make again",
  booksLinkAgainOf: (number: number) => `again of file ${number}`,
  booksReconciliationTitle: "Books Reconciliation",
  booksReconciliationIntro:
    "LAMP and Tally compared voucher by voucher, from a day book you export in Tally (Display > Day Book, export as XML) and send in chat. Nothing in it books or changes anything.",
  booksReconciliationPeriod: (from: string, to: string) => `${from} to ${to}`,
  booksReconciliationVersion: (version: number, at: string, dayBook: string) =>
    `Version ${version}, ${at}, from ${dayBook}`,
  booksReconciliationStatus: {
    matched: "Matched",
    differs: "Differs",
    only_in_lamp: "In LAMP, not in Tally",
    only_in_tally: "In Tally, not in LAMP",
    twice: "In Tally twice",
  },
  booksReconciliationDifference: { amount: "amount", ledger: "ledger", cost_centre: "cost centre" },
  booksReconciliationAllMatched: (count: number) =>
    count === 1
      ? "The one voucher is in both LAMP and Tally, and agrees."
      : `All ${count} vouchers are in both LAMP and Tally, and agree.`,
  booksReconciliationSheet: "Download the sheet",
  booksReconciliationShow: "Show the lines",
  booksReconciliationAgain: "Compare again",
  booksReconciliationBill: "Bill or voucher",
  booksReconciliationLamp: "LAMP",
  booksReconciliationTally: "Tally",
  booksReconciliationLookAt: "For the accounts Role to look at: not a bill.",

  // parties/Parties.tsx
  partiesTitle: "Customers and suppliers",
  partiesIntro: "Everyone you buy from, sell to or send job work to.",
  partiesShow: "Show",
  partiesAll: "All",
  partiesCustomers: "Customers",
  partiesSuppliers: "Suppliers",
  partiesJobWorkers: "Job workers",
  partiesCustomer: "Customer",
  partiesSupplier: "Supplier",
  partiesJobWorker: "Job worker",
  partiesSearch: "Search customers and suppliers",
  partiesWhat: "customers and suppliers",
  partiesNoMatch: (q: string) => `Nobody matches “${q}”.`,
  partiesNone: "Nobody here yet.",
  partiesName: "Name",
  partiesKind: "Kind",
  partiesGstin: "GSTIN",
  partiesPhone: "Phone",
  partiesPaysIn: "Pays in",
  partiesDays: (days: number) => (days === 1 ? "1 day" : `${days} days`),

  // items/Items.tsx
  itemsTitle: "Items and stock",
  itemsIntro: "Finished parts, raw material and consumables, with what's in the store.",
  itemsShow: "Show",
  itemsAll: "All",
  itemsInStock: "In stock",
  itemsOutOfStock: "Out of stock",
  itemsSearch: "Search items",
  itemsWhat: "Items",
  itemsNoMatch: (q: string) => `No Item matches “${q}”.`,
  itemsNone: "No Item here.",
  itemsItem: "Item",
  itemsHsn: "HSN",
  itemsGstRate: "GST rate",
  itemsStock: "In stock",
  /** An Item's rates and reorder level (#781): what it costs to buy and sells for, per its unit. */
  itemsRates: "Cost / selling price",
  itemsRatesOf: (cost: string, price: string, unit: string) => `${cost} / ${price} per ${unit}`,
  /** Only the cost, or only the selling price, where the person's Roles show the one and not the other (#1013). */
  itemsCost: "Cost",
  itemsSalePrice: "Selling price",
  itemsRateOf: (rate: string, unit: string) => `${rate} per ${unit}`,
  /** What confirmed orders not yet picked hold of an Item (#1013). */
  itemsHeld: "Held for orders",
  itemsReorder: "Reorder level",
  itemsReorderOf: (level: string, order: string | undefined, unit: string) =>
    `${level} ${unit}${order === undefined ? "" : `, order ${order} ${unit}`}`,

  // parties/MasterDataForm.tsx: adding and correcting customers, suppliers, job workers and Items
  masterAdd: "Add",
  masterEdit: "Edit",
  // The screen-reader header of a list's last column: Edit, Files and BOM.
  masterActions: "Actions",
  masterEditOf: (name: string) => `Edit ${name}`,
  masterSave: "Save",
  masterAddParty: "Add a customer, supplier or job worker",
  masterAddItem: "Add an Item",
  masterName: "Name",
  masterKind: "Kind",
  masterGstin: "GSTIN",
  masterAddress: "Address",
  masterState: "State",
  masterPhone: "Phone",
  masterEmail: "Email",
  masterTerms: "Payment terms, in days",
  masterItemKind: "Kind",
  masterItemKindNone: "Not said",
  masterFinishedPart: "Finished part",
  masterRawMaterial: "Raw material",
  masterConsumable: "Consumable",
  masterUnit: "Unit, such as kg or piece",
  masterHsn: "HSN",
  masterGstRate: "GST rate (%)",
  masterGstRateHelp: "Used where LAMP has no rate for the HSN; LAMP's own rate wins where it has one.",
  masterSupplier: "Bought from",
  masterOpening: "Opening stock",
  masterOpeningHelp: "Posted once to the stock Ledger.",
  masterOpeningUnit: "Counted in, if not the Item's unit",
  masterFailed: "Couldn't save it just now. Try again.",
  masterConfirm: "Confirm",
  masterCorrect: "Correct",
  masterRecorded: "Recorded",

  // A supplier's tax particulars on its page (#617): PAN, usual Nature of Payment and lower-deduction certificate.
  taxTitle: "Tax particulars",
  taxPan: "PAN",
  taxPanNone: "None on record",
  taxPanMasked: "Shown masked: your Roles don't hold ID numbers.",
  taxPanNew: "Set the PAN",
  taxPanHelp: "It must be the PAN the GSTIN carries. Leave it empty to keep the one on record.",
  taxPanClear: "Clear the PAN",
  taxNature: "Usual Nature of Payment",
  taxNatureNone: "Not said",
  taxNatureWords: (_id: string, words: string) => words.charAt(0).toUpperCase() + words.slice(1),
  taxCertificate: "Lower-deduction certificate",
  taxCertificateHelp: "Leave every box empty if the supplier holds none.",
  taxCertificateNumber: "Certificate number",
  taxCertificateRate: "TDS rate it allows (%)",
  taxCertificateLimit: "Up to (₹)",
  taxCertificateFrom: "Valid from",
  taxCertificateTo: "Valid to",
  taxSave: "Save tax particulars",
  taxBankDetails: "Bank details are changed only by an Owner, in person, with a fresh code.",
  taxFailed: "Couldn't read the tax particulars just now.",

  // A part's bill of materials and routing (#565): on the Item's page, a stopped material check's card and in chat.
  bomGive: "Give the BOM",
  bomButton: "BOM",
  bomOf: (name: string) => `Bill of materials and routing of ${name}`,
  bomForOne: (part: string, unit: string) => `What one ${unit} of ${part} is made of`,
  bomRouting: "Routing, in order",
  bomOutsourced: "At a job worker",
  bomJobWorker: "Job worker",
  bomNone: "No bill of materials yet.",
  bomSave: "Save the BOM",
  bomReadOnly: "Your Roles let you read it; the Floor Supervisor, Office Manager or Owner changes it.",
  bomFailed: "Couldn't read the bill of materials just now. Try again.",
  bomNotYours: "This part's bill of materials and routing are not yours to read: your Roles don't let you.",

  // The files kept against a customer, supplier, Item or Run (#575), on its page; a Run's also lists its order's (#818).
  keptFilesTitle: "Files",
  keptFilesOfOrder: "The order's files",
  keptFilesButton: "Files",
  keptFilesOf: (name: string) => `Files kept against ${name}`,
  keptFilesNone: "No files kept",
  keptFilesOpen: (name: string) => `Open ${name}`,
  keptFilesKeptAt: (when: string) => `Kept ${when}`,
  keptFilesKeptBy: (when: string, who: string) => `Kept ${when} by ${who}`,

  // runs/RunsList.tsx
  runsTitle: "Every run, past and present",
  runsIntro: "Search and filter every Value Stream run. Open one to see each step and exactly what was sent.",
  runsShow: "Show",
  runsAll: "All",
  runsLive: "Live",
  runsWaiting: "Waiting for you",
  runsFinished: "Finished",
  runsStageFinished: "Finished",
  runsRunning: "Running",
  runsLate: "Late",
  runsSearch: "Search runs",
  runsWhat: "Runs",
  runsNoMatch: (q: string) => `No Run matches “${q}”.`,
  runsNone: "No Run here yet.",
  runsRun: "Run",
  runsValueStream: "Value Stream",
  runsStage: "Stage",

  // runs/RunPanel.tsx
  runPanelMessage: "Message",
  runPanelDocument: "Document",
  runPanelPhoto: "Photo",
  runPanelChallan: "Challan",
  runPanelReceipt: "Receipt",
  runPanelFile: "File",
  runPanelArtefacts: "Artefacts",
  runPanelWhy: "Why?",
  runPanelNoWhy: "The Audit Log records who acted, and no rule, Playbook line or approval behind it.",
  runPanelThisRun: "This Run",
  runPanelUsedMiddle: "has used",
  runPanelUsedAfter: ".",
  runPanelCredits: (credits: string) => `${credits} Credits`,
  runPanelUnplaced: (credits: string) => ` ${credits} of them are not on a step.`,
  runPanelInvolved: "Everyone involved",
  runPanelCall: (name: string) => `Call ${name}`,
  runPanelAlsoInvolved: (names: string) => `Also involved: ${names}`,
  runPanelSteps: "Steps",
  runPanelNoSteps: "The Audit Log has nothing on this Run yet.",
  runPanelTheRun: "The Run",
  runPanelStep: (node: string) => `Step ${node}`,
  runPanelMessageTo: (party: string) => `Message to ${party}`,
  runPanelMessageFrom: (party: string) => `Message from ${party}`,
  runPanelSentOn: (channel: string) => `Sent on ${channel}, exactly as sent:`,
  runPanelReceivedOn: (channel: string) => `Received on ${channel}, exactly as received:`,
  runPanelForbidden: "Your Roles do not let you see this Run's panel.",
  runPanelNotARun: "That is not a Run.",
  runPanelFailed: "The Run could not be loaded. Try again shortly.",
  runPanelPartly: "Some of this Run's history or Credits could not be loaded. Try again shortly.",
  runPanelLoading: "Loading the Run…",
  /** Why a Run is stopped or on whom it waits, at the top of its page (#513). */
  runStanding: "Where this Run stands",
  runStandingATask: "a Task",
  runStandingBlocked: (task: string) => `Stopped at ${task}`,
  runStandingWaiting: (task: string) => `Waiting: ${task}`,
  runStandingPrepared: (task: string) => `LAMP prepared ${task}; it goes on once confirmed`,
  runStandingPaused: "Paused",
  runStandingHalted: "Halted: LAMP stopped this Run and raised an Incident",
  runStandingPausedBecause: {
    credits_exhausted: "The business's Credits have run out: it goes on once they are topped up.",
    kill_switch: "LAMP's agents are paused: it goes on once an Owner resumes them.",
    read_only: "The business is read-only for now.",
  } satisfies Readonly<Record<"credits_exhausted" | "kill_switch" | "read_only", string>>,
  runStandingOnYou: "It waits on you.",
  runStandingOnLamp: "LAMP is doing it.",
  runStandingOn: (role: string) => `It waits on the ${role}.`,
  runStandingOpen: "Open it",
  /** A Work Item's status, on the steps of a Run whose Audit Log the person does not read. */
  runStepStatus: {
    open: "to do",
    awaiting_approval: "waiting to be confirmed",
    blocked: "stopped",
    done: "done",
  } satisfies Readonly<Record<"open" | "awaiting_approval" | "blocked" | "done", string>>,
  runPanelCostHeading: "Cost and margin",
  runPanelCostOrder: (number: string, customer: string) => `Sales order ${number} for ${customer}`,
  runPanelCostCosts: "Costs",
  runPanelCostEstimated: "Estimated",
  runPanelCostSoFar: "Cost so far",
  runPanelCostProjected: "Heading for",
  runPanelCostMaterial: "Material",
  runPanelCostTime: "Time",
  runPanelCostJobWork: "Job work",
  runPanelCostTotal: "Total",
  runPanelMarginQuoted: (pct: string) => `Quoted margin: ${pct}`,
  runPanelMarginProjected: (pct: string) => `Margin it is heading for: ${pct}`,
  runPanelMarginAtRisk: (points: number) => `More than ${points} points below the quoted margin.`,
  runPanelCostCauses: "What cost more than planned",
  runPanelCostNoCauses: "Nothing was booked beyond the BOM and routing.",

  // runs/panel.ts
  runPanelSomeone: "someone",
  runPanelAChannel: "a Channel",
  runPanelObjectDocument: "Document",
  runPanelObjectInvoice: "Invoice",
  runPanelObjectDeliveryChallan: "Delivery challan",
  runPanelObjectChallan: "Challan",
  runPanelObjectReceipt: "Receipt",
  runPanelObjectQuotation: "Quotation",
  runPanelObjectSalesOrder: "Sales order",
  runPanelObjectPurchaseOrder: "Purchase order",
  runPanelObjectEnquiry: "Enquiry",
  runPanelObjectWorkOrder: "Work order",
  runPanelWhoAgent: (who: string, work: string) => `${who}, on LAMP's ${work}`,
  runPanelEnteredStep: (run: string, step: string) =>
    step ? `${upperFirst(run)} moved on to ${step}` : `${upperFirst(run)} moved on`,
  runPanelFileStarted: (name: string) => `${name}, handed to LAMP, started this`,
  runPanelFileKept: (name: string, words?: string) => `${name} kept here${words ? `: “${words}”` : ""}`,
  runPanelRunCompleted: (run: string) => `${upperFirst(run)} finished`,
  runPanelRunBlocked: (run: string) => `${upperFirst(run)} stopped and waits for someone`,
  runPanelRunPaused: (run: string) => `${upperFirst(run)} paused`,
  runPanelRunResumed: (run: string) => `${upperFirst(run)} went on again`,
  runPanelRunMigrated: (run: string) => `${upperFirst(run)} moved to the business's new way of working`,
  runPanelNudged: (role: string | undefined, run: string) =>
    `Reminded ${role ?? "the Role"} about late work on ${lowerFirst(run)}`,
  runPanelEscalated: (to: string | undefined, run: string) =>
    `Escalated ${lowerFirst(run)} to ${to ?? "someone above"}`,
  runPanelDeviationRaised: (run: string, what: string | undefined) =>
    `Asked to let ${lowerFirst(run)} through as it stands${what ? `: ${what}` : ""}`,
  runPanelDeviationApproved: (run: string, kind: string | undefined) =>
    `Let ${lowerFirst(run)} through as it stands${kind ? ` (${kind})` : ""}`,
  runPanelIssued: (name: string, number: string | undefined, run: string | undefined) =>
    `Issued ${name.toLowerCase()}${number ? ` ${number}` : ""}${run ? ` for ${lowerFirst(run)}` : ""}`,
  runPanelChecksPassed: "An agent's result passed its checks",
  runPanelChecksFailed: "An agent's result failed its checks",
  runPanelConfirmed: "Confirmed what an agent prepared",
  runPanelCorrected: "Corrected what an agent prepared",
  runPanelSentBack: "Sent back what an agent prepared",
  runPanelUndid: (reason: string | undefined) => `Undid an agent's work${reason ? `: ${reason}` : ""}`,
  runPanelSentMessage: (party: string, channel: string | undefined) =>
    channel ? `Sent a message to ${party} on ${channel}` : `Sent a message to ${party}`,
  runPanelReceivedMessage: (party: string, channel: string | undefined) =>
    channel ? `Received a message from ${party} on ${channel}` : `Received a message from ${party}`,
  runPanelRule: (rule: string) => `Rule: ${rule}`,
  runPanelPlaybookLine: (line: string) => `Playbook line: ${line}`,
  runPanelGoesOn: (from: string | undefined) =>
    from ? `The Value Stream goes on to this step after ${from}.` : "The Value Stream starts here.",
  runPanelBlockedBecause: (reason: string) => `Blocked because ${reason}`,
  runPanelOverdue: (expected: string | undefined) =>
    expected ? `Overdue: it was expected within ${expected}.` : "Overdue.",
  runPanelExpected: (expected: string) => `It was expected within ${expected}.`,
  runPanelApprovalRule: (approver: string) => `An Approval Rule sends it to ${approver} to approve.`,
  runPanelApprovedBy: (by: string) => `Approved by ${by}.`,
  runPanelConfirmedBy: (who: string) => `${who} confirmed it.`,

  // floor/Floor.tsx
  floorLate: "late",
  floorNeedsYou: "needs you",
  floorLive: (live: number) => `${live} live`,
  floorNeedYou: (count: number) => `${count} need you`,
  floorLateCount: (late: number) => `${late} late`,
  floorMoves: (driver: string) => `Moves ${driver}`,
  /** A Value Stream's name as the Floor shows it: the packs' own, which a language may put in its words (#1016). */
  floorStream: (name: string) => name,
  floorNothing: "Nothing here",
  floorFailed: "The Floor could not be loaded. Try again shortly.",
  floorLoading: "Loading the Floor…",
  floorTitle: "The floor, right now",
  floorIntro:
    "Each Value Stream is one card: its steps left to right, every live Run on the step it stands on. Outlined in amber means it's waiting for you.",
  floorNone: "No Value Stream is switched on yet. Set up LAMP to choose your Focus Areas.",

  // preview/Preview.tsx
  previewActive: "Active",
  previewCancelled: "Cancelled",
  previewSuspended: "Suspended",
  previewItem: "Item",
  previewQuantity: "Quantity",
  previewUnitPrice: "Unit price",
  previewAmount: "Amount",
  previewOrderValue: "Order value",
  previewNumber: "Number",
  previewDate: "Date",
  previewDeliveryBy: "Delivery by",
  previewCustomerPo: "Customer's PO",
  /** The customer's standing Notes, on an order or a pick for them (#510). */
  previewCustomerNotes: (customer: string) => `Noted on ${customer}`,
  previewBillTo: "Bill to",
  previewCustomerGstin: (gstin: string) => `, GSTIN ${gstin}`,
  previewUnregistered: ", unregistered",
  previewGstinCheck: "GSTIN check",
  previewGstinChecked: (status: string, on: string) => `${status} on the GST portal, checked ${on}`,
  previewPlaceOfSupply: "Place of supply",
  previewIntraState: "within the State: CGST and SGST",
  previewInterState: "across States: IGST",
  previewFrom: "From",
  previewSupplier: (name: string, gstin: string) => `${name}, GSTIN ${gstin}`,
  previewVehicle: "Vehicle",
  previewInvoiceLines: "Invoice lines",
  previewHsn: "HSN",
  previewTaxableValue: "Taxable value",
  previewGst: "GST",
  previewCgst: "CGST",
  previewSgst: "SGST",
  previewIgst: "IGST",
  previewRoundOff: "Round off",
  previewTotal: "Total to bill",
  previewAgainstInvoice: "Against invoice",
  previewTotalCredit: "Total to credit",
  previewAdvance: "Advance received",
  previewBalance: "Balance due",
  previewTo: "To",
  previewThrough: "Through",
  previewOrder: "Order",
  previewCustomer: "Customer",
  previewDrawing: "Drawing",
  previewRevision: "Revision",
  previewNoCustomer: "No customer on record",
  previewOpenDrawing: "Open the drawing",
  previewJournal: "Journal",
  previewReversesOn: "Reverses on",
  previewEntries: "The entries it posts",
  previewAccount: "Account",
  previewDebit: "Debit",
  previewCredit: "Credit",
  previewWorking: "The working",
  previewOrdered: "Ordered",
  previewToPick: "To pick and issue from stock",
  previewQuotation: "Quotation",
  previewValidUntil: "Valid until",
  previewQuoted: "Quoted",
  previewQuotationTotal: "Quotation total",
  previewMargin: "Margin",
  previewMarginPct: (pct: number) => `${pct}%`,
  previewCoverNote: "Covering note",
  previewEnquiry: "Enquiry",
  previewAskedFor: "Asked for",
  previewQuotationBy: "Quotation promised by",

  // preview/ActionPreview.tsx
  previewNotSent: (name: string) => `Not sent. ${name} gets nothing until you send it again.`,
  previewMessageTo: (name: string) => `Message to ${name}`,
  previewSendingIn: (name: string, seconds: number) => `Sending to ${name} in ${seconds} s.`,
  previewUndo: "Undo",
  emailUndone: (name: string) => `Undone: no email went to ${name}.`,
  emailAlreadySent: (name: string) => `Too late to undo: the email to ${name} has gone.`,
  previewSendTo: (name: string) => `Send to ${name}`,
  previewCallInstead: (name: string) => `Call ${name} instead`,
  previewCantIssue: (problems: string) => `It can't be issued yet: ${problems}.`,
  previewCantEdit: "It can't be edited once issued.",
  previewConfirmIssue: "Confirm and issue",
  previewConfirm: "Confirm",

  // preview/Failure.tsx
  failureUnreachable: "LAMP could not be reached, so nothing was done.",
  failureCheckConnection: "Check your connection and try again.",
  failureSignedOut: "You were signed out, so nothing was done.",
  failureSignIn: "Sign in again, then try again.",
  failureForbidden: "Your Roles don't allow this, so nothing was done.",
  failureAskOwner: "Ask an Owner to do it, or to give you a Role that may.",
  failureChanged: "It changed while you were looking, so nothing was done.",
  failureCheckAgain: "It now shows as it stands: check it and try again.",
  failureNotFinished: "LAMP could not finish it.",
  failureCheckHappened: "It now shows as it stands: check whether it happened before you try again.",
  failureRefused: (code: string) => `LAMP refused it (${code}).`,
  failureChange: "Change what it names and try again.",
  failureRetry: "Retry now",

  // kill-switch/KillSwitch.tsx
  killSwitchDefaultReason: "paused by the Owner",
  killSwitchHeld: (held: number) =>
    held === 1 ? "1 message is waiting to be sent." : `${held} messages are waiting to be sent.`,
  killSwitchUnreachable: "LAMP could not be reached. Check your connection and try again.",
  killSwitchByOperations: "LAMP operations paused the agents.",
  killSwitchResuming: "Resuming…",
  killSwitchResume: "Resume LAMP's agents",
  killSwitchPause: "Pause LAMP's agents",
  killSwitchExplain:
    "LAMP's agents stop at once. Runs wait at their next step and messages and filings are held until you resume. Everyone can still read everything and is told why.",
  killSwitchWhy: "Why (everyone sees this)",
  killSwitchPausing: "Pausing…",
  killSwitchPauseNow: "Pause now",

  // The modal panel.
  dialogClose: "Close",

  // The step-up dialog (ADR-0019): confirming it is you just before a sensitive action.
  stepUpTitle: "Confirm it is you",
  stepUpBankDetails: "You are about to change bank details: where money is paid.",
  stepUpRoles: "You are about to change which Roles a person holds.",
  stepUpOwner: "You are about to make someone an Owner of the business.",
  stepUpExport: "You are about to download all of the business's data.",
  stepUpPayment: "You are about to approve a payment.",
  stepUpPinChange: "You are about to set a new PIN.",
  stepUpOffboard: "You are about to offboard the business.",
  stepUpWhy: "LAMP asks you to confirm it is you first. This confirms this one action only.",
  stepUpSendCode: "Send me a code",
  stepUpSent: "LAMP sent a code to your Telegram or, if you have none, your email. Enter it here.",
  stepUpCode: "Code",
  stepUpConfirm: "Confirm",
  stepUpSendAgain: "Send a new code",
  stepUpOrPin: "Or use your PIN",
  stepUpConfirmPin: "Confirm with my PIN",
  stepUpWrongCode: "That code is wrong, has expired or was already used. Check it, or send a new code.",
  stepUpWrongPin: "That PIN is wrong. Try again, or use a code.",
  stepUpCodeRequired: "Only a code confirms this, not your PIN.",
  stepUpTooManyCodes: "You asked for too many codes. Wait a few minutes, then send a new one.",
  stepUpTooManyTries: "Too many wrong tries. Wait a few minutes, then try again.",
  stepUpNoMethod: "LAMP has no confirmed email or Telegram for you, so it cannot send you a code.",
  stepUpSignedOut: "You are signed out. Sign in again, then start over.",
  stepUpClosed: "Nothing was done: the confirmation was closed before it was finished.",
  // Shared by the owner pages: why an action did not happen.
  ownerPagesRefused: (code: string) => `LAMP refused it (${code})`,
  ownerPagesUnreachable: "LAMP could not be reached. Check your connection and try again.",

  // Memory
  memoryViaChat: "via chat",
  memoryViaWhatsapp: "via WhatsApp",
  memoryViaEmail: "via email",
  memoryViaPhone: "via a call",
  memoryInPerson: "in person",
  memoryViaCockpit: "via the Cockpit",
  memoryVia: (channel: string) => `via ${channel}`,
  memorySource: (by: string, byLamp: boolean, day: string, where: string) =>
    `${by}${byLamp ? " (saved by LAMP)" : ""}, ${day}, ${where}`,
  memoryForget: "Forget",
  memoryForgetLabel: (what: string) => `Forget ${what}`,
  memoryForgetAsk: (what: string) => `Forget ${what}?`,
  memoryForgetWarning: "LAMP deletes it for good.",
  memoryForgetIt: "Forget it",
  memoryKeepIt: "Keep it",
  memoryAddNote: "Add a Note",
  memoryNoteHint: "A fact LAMP cannot work out, such as a preferred language",
  memorySaveNote: "Save Note",
  memoryAddRelationshipNote: "Add a Relationship Note",
  memoryRelationshipRules:
    "For courtesy only: reminders, tone and timing. Never used to decide credit, prices, approvals or priority. LAMP never keeps caste, politics, sexual life, biometric or genetic data or anyone's finances, and religion only as a greeting preference.",
  memoryContact: "Contact",
  memoryKind: "Kind",
  memoryNote: "Note",
  memorySaveRelationshipNote: "Save Relationship Note",
  memoryCategoryFamilyEvent: "Family event",
  memoryCategoryHealth: "Health (kept 90 days)",
  memoryCategoryFestivalGreeting: "Festival greeting",
  memoryCategoryGreetingPreference: "Greeting preference",
  memoryCategoryCourtesy: "Other courtesy",
  memoryNoBehaviour: "Nothing paid yet, so there is no payment behaviour to show.",
  memoryBehaviour: (onTimePercent: number, paid: number, daysLate: number, overdue: number) =>
    `Paid on time ${String(onTimePercent)}% of ${String(paid)} ${paid === 1 ? "time" : "times"}; ${String(daysLate)} ${daysLate === 1 ? "day" : "days"} late on average when late; ${String(overdue)} overdue now.`,
  memoryFromLedgers: "Calculated from your Ledgers.",
  memoryLoading: "Loading what LAMP knows…",
  memoryForgotten: "Forgotten.",
  memoryKnowsAbout: (name: string) => `What LAMP knows about ${name}`,
  memoryBehaviourHeading: "Behaviour",
  memoryNotes: "Notes",
  memoryNoNotes: "No Notes yet.",
  memoryNoteSaved: "Note saved.",
  memoryRelationshipNotes: "Relationship Notes",
  memoryNoRelationshipNotes: "No Relationship Notes.",
  memoryKeptUntil: (day: string) => `Kept until ${day}.`,
  memoryRenewLabel: (text: string) => `Keep "${text}" another 90 days`,
  memoryRenew: "Renew",
  memoryRenewed: "Kept another 90 days.",
  memoryRelationshipNoteSaved: "Relationship Note saved.",
  memoryAccepted: (headline: string) => `${headline} Confirm it in Settings to apply it.`,
  memoryAcceptedPlain: "Accepted.",
  memoryDismissed: "Dismissed. LAMP will not suggest it again for 30 days.",
  memoryNoSuggestions: "No Suggestions this week.",
  memorySuggestions: "Suggestions",
  memoryAcceptLabel: (title: string) => `Accept: ${title}`,
  memoryPreviewChange: "Preview the change",
  memoryTakeALook: "Take a look",
  memoryDismissLabel: (title: string) => `Dismiss: ${title}`,
  memoryNotNow: "Not now",
  memoryIntro:
    'What LAMP knows is calculated from your records, noted with who said it, or suggested for you to accept. Ask in chat "what do you know about…?" or "forget that".',
  memoryCounterparty: "Counterparty",
  memoryChooseCounterparty: "Choose a Counterparty",

  // Insights: KPI values and how they moved
  insightsPercentagePoints: (value: string) => `${value} percentage points`,
  /** An amount of a unit the API names, singular for exactly one: "1 day", "6 days". */
  insightsAmount: (value: string, unit: string, one: boolean) =>
    `${value} ${one && unit.endsWith("s") ? unit.slice(0, -1) : unit}`,
  insightsPoints: (value: string, one: boolean) => `${value} ${one ? "point" : "points"}`,
  insightsNoFigureToCompare: (month: string) => `no figure for ${month} to compare with`,
  insightsSameAs: (month: string) => `the same as ${month}`,
  insightsSlower: (by: string, month: string) => `${by} slower than ${month}`,
  insightsFaster: (by: string, month: string) => `${by} faster than ${month}`,
  insightsHigher: (by: string, month: string) => `${by} higher than ${month}`,
  insightsLower: (by: string, month: string) => `${by} lower than ${month}`,
  insightsBetter: (moved: string) => `${moved}, which is better`,
  insightsWorse: (moved: string) => `${moved}, which is worse`,
  insightsNoFigureYet: (month: string) => `No figure for ${month} yet`,
  insightsLastFigure: (month: string, value: string) => `. ${month}: ${value}`,
  /** A KPI's name, as the packs name it (#1016). */
  insightsKpiName: (name: string) => name,
  /** Why a KPI is not measured, or has no value, as the API says it (#1016). */
  insightsReason: (reason: string) => reason.charAt(0).toUpperCase() + reason.slice(1),
  insightsWhatMovedIt: "What moved it",
  insightsNothingStoodOut: (month: string) => `No single record stood out in ${month}.`,
  insightsLoadFailed: "Insights could not be loaded. Try again shortly.",
  insightsLoading: "Loading Insights…",
  insightsHeading: "How your business is moving",
  insightsRange: (from: string, to: string) => `${from} to ${to}. `,
  insightsIntro: "Each thing you want to improve, with how its numbers moved and what moved them.",

  // Expenses: a month's expenses by account against the values of a Dimension.
  expensesHeading: (label: string) => `Expenses by ${label}`,
  expensesIntro: "What was spent in the month, by expense account. Open a figure to see the entries behind it.",
  expensesMonth: "Month",
  expensesSplitBy: "Split by",
  expensesDownload: "Download as a sheet",
  expensesLoading: "Loading expenses…",
  expensesLoadFailed: "The expense report could not be loaded. Try again shortly.",
  expensesNothing: (from: string, to: string) => `No expenses were posted from ${from} to ${to}.`,
  expensesPayrollHidden: "Payroll is left out: your Roles don't show it.",
  expensesAccount: "Account",
  expensesTotal: "Total",
  expensesUnallocated: "Unallocated",
  /** A figure's name, for the button that opens its entries and the entries' heading. */
  expensesFigure: (row: string, column: string) => `${row}, ${column}`,
  expensesDate: "Date",
  expensesDocument: "Document",
  expensesSupplier: "Supplier",
  expensesAmount: "Amount",
  expensesKind: "Kind",
  expensesDocumentHeading: "The Document",
  expensesDocumentRefused: "That Document could not be opened.",
  expensesClose: "Close",
  // Expense Bills: every supplier's bill for an expense, where it stands and what it belongs to.
  billsIntro: "Every supplier's bill for an expense: where it stands, who it waits on and what it belongs to.",
  billsAdd: "Add a bill",
  billsAdding: "Reading the bill…",
  billsAddWords: "Enter this bill",
  // A bill added that LAMP couldn't draft: what can be done with it on the page itself (#1006).
  billsTryAgain: "Try again",
  billsEnterByHand: "Enter it by hand",
  billsNotABill: "It isn't a bill",
  billsNotABillHint: "Not a bill? Say so here, then tell LAMP in Chat what it is.",
  billsTyped: (name: string) => `Enter the bill in ${name} by hand`,
  billsTypedSupplier: "Supplier, as on record",
  billsTypedNumber: "Bill number",
  billsTypedDate: "Bill date",
  billsTypedFor: "What it is for",
  billsTypedAmount: "Amount before tax (₹)",
  billsTypedGst: "GST (₹)",
  billsTypedTotal: "Total (₹)",
  billsTypedDraft: "Draft the bill",
  billsTypedCancel: "Cancel",
  billsTypedNotRupees: (field: string) => `${field}: give it in rupees, such as 1180.50`,
  billsTypedAddsUp: "The amount before tax and the GST must add up to the total.",
  billsFilters: "Filter the bills",
  billsStandingFilter: "Status",
  billsAnyStanding: "Any status",
  billsSupplierFilter: "Supplier",
  billsAnySupplier: "Any supplier",
  billsValueFilter: "Belongs to",
  billsAnyValue: "Anything",
  billsFrom: "From",
  billsTo: "To",
  billsToConfirm: "Waiting to be confirmed",
  billsToApprove: "Waiting for approval",
  billsToConfirmTax: "Waiting for tax",
  billsUnpaid: "Approved and unpaid",
  billsPaid: "Paid",
  billsHeld: "Held",
  billsDate: "Date",
  billsSupplier: "Supplier",
  billsNumber: "Bill",
  billsTotal: "Total",
  billsTds: "TDS",
  billsPayable: "Payable",
  billsStatus: "Status",
  billsWaitsOn: "Waits on",
  billsYou: "You",
  billsBelongsTo: "Belongs to",
  billsOpen: (number: string, supplier: string) => `Open bill ${number} from ${supplier}`,
  billsTotals: (count: number) =>
    count === 1
      ? "Posted: the 1 approved or paid bill these filters keep"
      : `Posted: the ${count} approved or paid bills these filters keep`,
  billsWaitingTotal: (count: number) =>
    count === 1
      ? "Not posted yet: the 1 bill waiting to be confirmed or approved"
      : `Not posted yet: the ${count} bills waiting to be confirmed or approved`,
  billsInNeither: (count: number) =>
    count === 1
      ? "1 bill held, or refused by LAMP, is in neither total."
      : `${count} bills held, or refused by LAMP, are in neither total.`,
  billsAmountsHidden: "Amounts are left out: your Roles don't show costs.",
  billsEmpty:
    "No bills yet. To add the first, press “Add a bill” and choose the supplier's bill as a PDF or a photo, or attach it in Chat with LAMP.",
  billsNoneMatch: "No bills match these filters.",
  billsLoading: "Loading bills…",
  billsLoadFailed: "The bills could not be loaded. Try again shortly.",
  billsPage: (page: number, pages: number) => `Page ${page} of ${pages}`,
  billsPrevious: "Previous page",
  billsNext: "Next page",
  billsLines: "Lines",
  billsDescription: "Description",
  billsAccount: "Expense account",
  billsAmount: "Amount",
  billsSplit: "Split",
  billsTax: "GST",
  billsHeldBecause: (reason: string) => `Held: ${reason}`,
  billsFile: "The bill",
  billsOpenFile: (name: string) => `Open ${name}`,
  billsPostings: "Posted to the Ledgers",
  billsNotPosted: "Not posted yet: a bill posts once it is approved.",
  billsOpenRun: "Open its Run",
  billsClose: "Close",
  insightsEmpty: "Nothing to show yet. Insights appear once your Focus Areas are set up.",
  trendCaption: (name: string) => `${name}, by month`,
  trendMonth: "Month",
  trendNoFigure: "No figure",

  // Plan and billing
  billingLoading: "Loading Plan and billing…",
  billingIntro:
    "Your plan, what it includes and what you have paid. You pay on Razorpay's own page: LAMP never sees your card or bank details.",
  billingNoPayments: "This LAMP installation does not take payments yet.",
  billingYourPlan: "Your plan",
  billingNoPlan: "No paid plan yet.",
  billingPlanLine: (name: string, annual: boolean) => `${name}, paid ${annual ? "yearly" : "monthly"}`,
  billingPaidFor: (from: string, to: string) => `Paid for ${from} to ${to}.`,
  billingNextCharge: (date: string) => `Next charge on ${date}, by your UPI Autopay or card mandate.`,
  billingChargeFailed: "The last charge failed. Razorpay will try again: check your UPI Autopay or card mandate.",
  billingHalted: "Razorpay stopped trying to charge. Subscribe again to keep using LAMP.",
  billingCancelledStatus: "Cancelled: nothing more is charged.",
  billingOverdue: (since: string) =>
    `LAMP's fees have been unpaid since ${since}. Pay to keep the business working: after 22 days unpaid it becomes read-only.`,
  billingReadOnly: "The business is read-only until LAMP's fees are paid. You can still see and export everything.",
  billingThisMonth: "This month",
  billingPeople: (active: string, included: string) => `${active} of ${included} people active`,
  billingRegistrations: (count: string, included: string) => `${count} of ${included} GST Registrations`,
  billingCreditsLine: (used: string, included: string, bought: string) =>
    `${used} Credits used, of ${included} in your plan and ${bought} bought`,
  billingExtraPeople: (count: string, month: string, price: string) =>
    `${count} extra active people in ${month}, at ${price} each plus GST, on the next charge`,
  billingHistory: "Payment history",
  billingNoHistory: "No payments yet.",
  billingWhen: "Date",
  billingWhat: "For",
  billingAmount: "Amount",
  billingReference: "Razorpay reference",
  billingFailedCharge: "Charge failed",
  billingChoosePlan: "Choose a plan",
  billingPlanOffer: (name: string, people: string, credits: string) =>
    `${name}: ${people} people, ${credits} Credits a month`,
  billingMonthly: (total: string) => `Monthly, ${total} with GST`,
  billingAnnual: (total: string) => `Yearly, ${total} with GST`,
  billingOnlyHolder: "Only the Account Holder subscribes to a plan or cancels it.",
  billingBuyCredits: "Buy Credits",
  billingPackOffer: (credits: string, total: string) => `${credits} Credits for ${total} with GST`,
  billingConfirm: "Confirm the amount",
  billingPrice: "Price",
  billingGst: "GST at 18%",
  billingTotal: "Total",
  billingPay: (total: string) => `Pay ${total} on Razorpay`,
  billingBack: "Back",
  billingPaid: "Paid. Thank you.",
  billingCancelPlan: "Cancel plan",
  billingCancelQuestion: "Cancel the plan? Nothing more is charged, and the period you paid for runs to its end.",
  billingCancelYes: "Yes, cancel the plan",
  billingCancelled: (until: string | undefined) =>
    until === undefined ? "Cancelled." : `Cancelled. Your plan runs until ${until}.`,
  billingInvoices: "Tax invoices",
  billingNoInvoices: "No tax invoices yet. LAMP's invoice for each payment appears here, and is emailed to you.",
  billingInvoiceNumber: "Invoice",
  billingInvoicePdf: "PDF",
  billingInvoiceOpen: (number: string) => `Open ${number}`,
  billingInvoiceTo: "Invoices are made out to",
  billingInvoiceToNone: "Not chosen yet: choose below, or LAMP cannot issue your invoices.",
  billingInvoiceToLine: (name: string, address: string, gstin: string | null) =>
    `${name}, ${address}${gstin ? `, GSTIN ${gstin}` : ", without a GSTIN"}`,
  billingInvoiceToChange: "Choose what LAMP's invoices carry",
  billingInvoiceToHolder: "Only the Account Holder chooses what LAMP's invoices carry.",
  billingInvoiceToRegistration: "GSTIN billed",
  billingInvoiceToNoGstin: "None: an invoice without a GSTIN",
  billingInvoiceToName: "Legal name",
  billingInvoiceToAddress: "Address",
  billingInvoiceToState: "State code (two digits)",
  billingInvoiceToSave: "Save",
  billingInvoiceToSaved: "Saved. LAMP's next invoices carry these particulars.",
  billingGstinNeedsCorrecting: (gstin: string) =>
    `GSTIN ${gstin} needs correcting: its last character is not the check character, so it was mistyped. LAMP's invoices do not carry it until it is corrected.`,
  billingGstinCorrectIn: "Correct it in Settings, under Your business on invoices",

  // Credits
  creditsPack: (credits: string, price: string) => `${credits} Credits for ${price}`,
  creditsConfirmTopUp: "Confirm top-up",
  /** The question around the price, which is shown in bold: [before it, after it]. */
  creditsPayQuestion: (credits: string): readonly [string, string] => [
    "Pay ",
    ` + 18% GST for ${credits} Credits this month?`,
  ],
  creditsPay: (price: string) => `Pay ${price}`,
  creditsCancel: "Cancel",
  creditsBuy: (pack: string) => `Buy ${pack}`,
  creditsAutoRecharge: "Top up automatically when 95% of this month's Credits are used, so Runs never pause",
  creditsPackToBuy: "Pack to buy",
  creditsLoading: "Loading Credits…",
  creditsIntro: "Every step a Run takes costs 0.1 Credit; each kind of task an agent does has its own fixed price.",
  /** The Credits left around the number left, which is shown in bold: [before it, after it]. */
  creditsLeft: (budget: string, included: string, bought: string | undefined): readonly [string, string] => [
    "",
    ` of ${budget} Credits left (${included} in your plan${bought === undefined ? "" : `, ${bought} bought`}).`,
  ],
  creditsUsedThisMonth: "Credits used this month",
  creditsUsedPercent: (percent: number) => `${String(percent)}% used`,
  creditsRunOut:
    "Your Credits have run out. Runs wait at their next step until you top up; everyone can still read and work by hand.",
  creditsByKind: "By kind of task",
  creditsNothingUsed: "Nothing has used Credits this month yet.",
  creditsKindOfTask: "Kind of task",
  creditsModelTier: "Model tier",
  creditsTimes: "Times",
  creditsCredits: "Credits",
  creditsNoModel: "No model",
  creditsTopUp: "Top up",
  creditsAdded: (credits: string) => `Added ${credits} Credits to this month.`,
  creditsAutoRechargeOff: "Auto-recharge is off.",
  creditsAutoRechargeOn: "Auto-recharge is on.",
  creditsTopUps: "Top-ups this month",
  creditsAutomatic: " (auto-recharge)",
  creditsNoAnswerToday: "No answer from LAMP's model today",
  /** Today's calls with no completion: the first few a day are free, LAMP's cost; the rest use Credits (#929). */
  creditsNoAnswerCounts: (free: string, charged: string, credits: string, perDay: string) =>
    `${free} free, at LAMP's cost; ${charged} used ${credits} Credits. The first ${perDay} a day are free.`,

  // Audit log
  /** The columns as the page shows them, then the ids behind them. */
  auditCsvColumns: [
    "Entry",
    "When",
    "What",
    "Who",
    "Run",
    "Step",
    "Business Object",
    "Action",
    "Person id",
    "Agent task class",
    "Run id",
    "Node",
    "Business Object id",
  ],
  auditOpenRun: "Open the Run",
  auditTheRun: "the Run",
  auditRunWith: (name: string) => `the Run with ${name}`,
  /** A person the Directory has no name for: never shown by their id. */
  auditAPerson: "a person",
  auditLampDoing: (work: string) => `LAMP (${work})`,
  auditRunHalted: (run: string) => `${upperFirst(run)} was halted for an incident`,
  auditIncidentRaised: (run: string) => `Raised an incident on ${lowerFirst(run)}`,
  auditIncidentResolved: (run: string) => `Resolved the incident on ${lowerFirst(run)}`,
  auditDeviationDeclined: (run: string, reason: string | undefined) =>
    `Did not let ${lowerFirst(run)} through${reason ? `: ${reason}` : ""}`,
  auditSuggestionRaised: (run: string) => `Suggested making a rule of letting through orders like ${lowerFirst(run)}`,
  auditAgentStepTaken: (run: string) => `Prepared work on ${lowerFirst(run)}`,
  auditJudgeSampled: (run: string) => `Had a second model check its work on ${lowerFirst(run)}`,
  auditRunStarted: (run: string) => `Started work on ${lowerFirst(run)}`,
  auditTaskOpened: (task: string, run: string) => `Opened “${task}” on ${lowerFirst(run)}`,
  auditTaskConfirmed: (task: string, run: string, edited: boolean) =>
    `${edited ? "Corrected and confirmed" : "Confirmed"} “${task}” on ${lowerFirst(run)}`,
  auditTaskSentBack: (task: string, run: string, reason: string | undefined) =>
    `Sent “${task}” on ${lowerFirst(run)} back${reason ? `: ${reason}` : ""}`,
  auditTaskDone: (task: string, run: string) => `Finished “${task}” on ${lowerFirst(run)}`,
  auditHoldReleased: (run: string, reason: string | undefined) =>
    `Released ${lowerFirst(run)} from its hold${reason ? `: ${reason}` : ""}`,
  auditPosted: (name: string, number: string | undefined, run: string | undefined) =>
    `Posted ${name.toLowerCase()}${number ? ` ${number}` : ""} to the Ledgers${run ? ` for ${lowerFirst(run)}` : ""}`,
  /** A Task the entry does not name. */
  auditATask: "a Task",
  auditActions: AUDIT_ACTIONS,
  auditRun: "Run",
  auditBusinessObject: "Business Object",
  /** An entry about the business itself, such as its Supplier Particulars. */
  auditTheBusiness: "The business",
  auditPerson: "Person (or system)",
  auditAgent: "Agent task class",
  auditFrom: "From",
  auditTo: "To",
  auditIntro: "Everything done in your business, by a person or by LAMP, oldest first.",
  auditFilterLabel: "Filter the Audit log",
  auditFilter: "Filter",
  auditExport: "Export CSV",
  auditLoading: "Loading the Audit log…",
  auditNoEntries: "No entries match.",
  auditWhen: "When",
  auditWhat: "What",
  auditWho: "Who",
  auditShowMore: "Show more",

  // Users
  usersNever: "Never",
  usersName: "Name",
  usersEmail: "Email",
  usersMobile: "Mobile number",
  usersInviteBy: "Invite by",
  usersRoles: "Roles",
  usersInvite: "Invite someone",
  usersThisPerson: "this person",
  usersThatPerson: "that person",
  usersChangeRolesOf: (who: string) => `Change Roles of ${who}`,
  usersChange: "Change",
  usersRolesOf: (who: string) => `Roles of ${who}`,
  usersSaveRoles: "Save Roles",
  usersCancel: "Cancel",
  usersLoading: "Loading Users…",
  usersEveryone: "Everyone",
  usersActive: "Active",
  usersInvited: "Invited",
  usersIntro:
    "Everyone who works with LAMP in your business: how they sign in, where they talk to LAMP, and what they may do.",
  usersShow: "Show",
  /** A Role no active person holds, and what waits on it on the Owners' Today (#567). */
  usersNoOneHolds: "No one holds these Roles yet: what waits on them comes to you",
  usersRoleWaiting: (role: string, waiting: number) =>
    `${role}: ${waiting === 0 ? "nothing waiting" : waiting === 1 ? "1 waiting" : `${waiting} waiting`}`,
  usersInvitedNotice: (email: string) =>
    `Invited ${email}. They get an email with a link that signs them in; it works once, for 7 days.`,
  usersInvitedMobileNotice: (who: string, mobile: string, code: string) =>
    `Invited ${who}. Ask them to open LAMP's Telegram bot on the phone with ${mobile} and send /start ${code}`,
  usersRolesChanged: (who: string) => `Changed the Roles of ${who}.`,
  usersPerson: "Person",
  usersRole: "Role",
  usersTalksOn: "Talks to LAMP on",
  usersSignsInWith: "Signs in with",
  usersLastActive: "Last active",
  usersStatus: "Status",
  usersAccountHolder: "Account Holder",
  usersInviteSent: "Invite sent",
  usersSendAgain: "Send the invitation again",
  usersSendAgainTo: (who: string) => `Send the invitation to ${who} again`,
  usersSentAgain: (who: string) => `Sent ${who} a new invitation. The one before no longer works.`,
  usersSentAgainMobile: (who: string, code: string) =>
    `Made ${who} a new invitation; the one before no longer works. Ask them to open LAMP's Telegram bot on the phone with their number and send /start ${code}`,
  usersWhenInvited: "When someone is invited",
  usersWhenInvitedText:
    "They sign in with a code LAMP emails them, and see only what their Roles allow. A person may hold several Roles, and several people may be Owners; one Owner is the Account Holder, who looks after the subscription.",

  // Connections
  connectionsLoading: "Loading Connections…",
  connectionsIntro:
    "The AI assistants, such as Claude or ChatGPT, connected to LAMP. Each acts as the person who connected it, never more than their Roles, and anything it asks to do waits for that person to confirm in LAMP. Revoke one and its next call is refused.",
  connectionsNone: "No assistant is connected.",
  connectionsPerson: "Person",
  connectionsAssistant: "Assistant",
  connectionsHost: "Signs in from",
  connectionsMay: "May",
  connectionsConnected: "Connected",
  connectionsLastUsed: "Last used",
  connectionsRead: "Read",
  connectionsReadAndAsk: "Read, and ask to do things",
  connectionsYou: "You",
  connectionsRevoke: "Revoke",
  connectionsRevokeOf: (assistant: string, who: string) => `Revoke ${assistant} for ${who}`,
  connectionsRevoked: (assistant: string) => `Revoked ${assistant}. Its next call to LAMP is refused.`,

  // Roles
  rolesApprove: "Approve",
  rolesAct: "Act",
  rolesSee: "See",
  rolesCostsAndMargins: "Costs and margins",
  rolesPayroll: "Payroll",
  rolesBankAndCash: "Bank and cash balances",
  rolesCustomerPricing: "Customer pricing",
  rolesLoading: "Loading Roles…",
  rolesIntro:
    "What each Role may see, do and approve across your business. Change it by chatting, e.g. “let Floor Supervisor see Goods Receipt” or “show costs and margins to Office Manager”.",
  rolesWhatEachMayDo: "What each Role may do",
  rolesArea: "Area",
  rolesSensitiveData: "Sensitive data",
  rolesSensitiveDataEach: "Sensitive data each Role sees",
  rolesKindOfData: "Kind of data",
  rolesYes: "Yes",
  rolesNo: "No",
  rolesHide: "Hide",
  rolesShow: "Show",
  rolesHideFrom: (data: string, role: string) => `Hide ${data.toLowerCase()} from ${role}`,
  rolesShowTo: (data: string, role: string) => `Show ${data.toLowerCase()} to ${role}`,
  rolesWhoApproves: "Who approves what",
  rolesWhen: "When",
  rolesGoesTo: "Goes to",
  rolesVersion: (version: string) => `Business Tree version ${version}.`,

  // Set up (onboarding)
  onboardingMostImportant: "Most important",
  onboardingThen: "Second",
  onboardingOptional: "Third (optional)",
  onboardingQuestion: "Onboarding question",
  /** A Set up LAMP question in the person's language, naming the GSTIN it could not look up; English as the api asks it. */
  onboardingPrompt: promptAsAsked,
  /** One of a question's choices in the person's language; English as the api names it. */
  onboardingOption: (_question: string, option: { readonly id: string; readonly label: string }): string =>
    option.label,
  onboardingChoose: "Choose…",
  onboardingGstin: "GSTIN",
  onboardingLegalName: "Registered business name",
  onboardingAddress: "Registered address",
  onboardingState: "State code of the address (two digits, as the GSTIN starts)",
  onboardingSignatory: "Authorised signatory",
  onboardingInvoicesCarry: (name: string, address: string, gstin: string, signatory: string) =>
    `Your invoices will carry: ${name}, ${address}, GSTIN ${gstin}, signed by ${signatory}. You can change these in Settings.`,
  onboardingNext: "Next",
  onboardingNames: "One name on each line. Leave it empty to add none now.",
  onboardingAdds: (label: string, names: readonly string[]) => `Under ${label}, LAMP will add: ${names.join(", ")}.`,
  onboardingReady: "Ready to switch on",
  onboardingTemplate: (vertical: string | undefined) =>
    `LAMP will set up your business${vertical === undefined ? "" : ` for ${vertical}`} and switch on:`,
  /** A Vertical Template by its pack id, as the Owner reads it; undefined for one without words here. */
  onboardingVertical: (id: string): string | undefined => (id === "mto" ? "make-to-order manufacturing" : undefined),
  /** A Business Profile value in plain words; undefined for one without words here. */
  onboardingProfile: (key: string, value: string): string | undefined => PROFILE_WORDS[key]?.[value],
  onboardingFromAnswers: (answers: string, trialDays: number) =>
    `From your answers: ${answers}. Your free trial runs for ${String(trialDays)} days; no payment details are needed.`,
  onboardingOff: (names: readonly string[]) =>
    `Off for now: ${names.join(", ")}. You can switch any of them on later by saying so in chat, for example "switch on ${names[0] ?? "Kaizen Loop"}".`,
  /** What does not happen while a Value Stream set-up leaves off is off (#784); undefined for one without words here. */
  onboardingWhileOff: (id: string, name: string): string | undefined =>
    ({
      VS22: `While ${name} is off, nothing starts in March: no stock count on the year's last day and no reminder to close the year's books or renew what expires.`,
      VS19: `While ${name} is off, no month's attendance is locked for payroll.`,
      VS21: `While ${name} is off, nothing reminds the Accountant of the quarter's returns and advance tax.`,
      VS5: `While ${name} is off, orders are made from work orders without a production plan or job cards.`,
    })[id],
  onboardingSwitchOn: "Switch it on",
  onboardingSetUp: "LAMP is set up.",
  onboardingTrialUntil: (day: string) => ` Your free trial runs until ${day}.`,
  onboardingWaitlisted: "Thank you. You are on the waitlist, and we will tell you when LAMP can serve you.",
  // Settings
  settingsUnreachable: "LAMP could not be reached. Check your connection and try again.",
  settingsRefused: (code: string) => `LAMP refused it (${code})`,
  settingsYes: "Yes",
  settingsNo: "No",
  settingsEnterNumber: "Enter a number.",
  settingsEnterWholeNumber: "Enter a whole number.",
  settingsLeast: (min: number) => `The least it can be is ${min}.`,
  settingsMost: (max: number) => `The most it can be is ${max}.`,
  settingsAtLeast: (min: number) => `at least ${min}`,
  settingsAtMost: (max: number) => `at most ${max}`,
  /** Before a Parameter's default, which is shown as the pack's own value. */
  settingsDefault: "default",
  settingsLockedBy: (by: string) => `Locked by ${by}: required by law or the template`,
  settingsPreviewChange: "Preview change",
  settingsLoading: "Loading Settings…",
  settingsTreeVersion: (version: string) =>
    `Business Tree version ${version}. Every change is previewed before it applies.`,
  settingsWaiting: "Changes waiting for you",
  settingsNothing: "Nothing here can be set yet.",

  // The business's Supplier Particulars (#342), in Settings, Owner only
  particularsHeading: "Your business on invoices",
  particularsHelp:
    "Every tax invoice your business issues prints these. The GSTIN's first two digits must be the address's State.",
  particularsNone: "Not set up yet: invoices cannot be issued until they are.",
  particularsLegalName: "Legal name",
  particularsAddress: "Address",
  particularsState: "State code (two digits)",
  particularsGstin: "GSTIN",
  particularsSignatory: "Authorised signatory",
  particularsTurnover: "Aggregate turnover last financial year (₹)",
  particularsSave: "Save",
  particularsSaved: "Saved: invoices issued from now on carry these.",

  // Settings → Dimension values and Expense accounts (V-10): the same values and accounts chat keeps
  shellDimensionValues: "Dimension values",
  shellExpenseAccounts: "Expense accounts",
  listName: "Name",
  listAdd: "Add",
  listRename: "Rename",
  listRetire: "Retire",
  listSave: "Save",
  listCancel: "Cancel",
  listNewName: (name: string) => `New name for ${name}`,
  listRetireSure: (name: string) =>
    `Retire “${name}”? It stays on everything posted with it, and can't be chosen for new work.`,
  listRetireConfirm: "Retire it",
  listRetired: "Retired",
  listAdded: (name: string, under: string) => `“${name}” is added under ${under}.`,
  listRenamed: (from: string, to: string) => `“${from}” is now “${to}”.`,
  listRetiredNotice: (name: string) => `“${name}” is retired: it stays on what was posted with it.`,
  listReadOnly: "Your Roles let you see these, not change them.",
  listNotAllowed: "Your Roles don't let you do this: the Owner or someone who keeps the accounts can.",
  dimensionsHelp:
    "The values your business tags its bills and entries with, by Dimension. A retired value stays on what was posted with it and can't be chosen for new work.",
  dimensionsEmpty: (label: string) => `Nothing under ${label} yet.`,
  dimensionsCode: "Code (optional)",
  dimensionsCodeShown: (code: string) => `Code ${code}`,
  dimensionsStandsFor: (name: string) => `Stands for ${name}`,
  dimensionsStandsForHidden: "Stands for a customer or supplier",
  dimensionsAddTo: (label: string) => `Add a value to ${label}`,
  accountsHelp:
    "The pack's accounts can't be changed here. Add expense accounts of your own under its groups: the trial balance and the profit and loss account show them there.",
  accountsYours: "Yours",
  accountsPack: "From the pack",
  accountsGroup: "Group",
  accountsAdd: "Add an expense account",

  // Settings → Ledger Mapping (V-12): each account, supplier, customer and Dimension value's name in Tally
  shellLedgerMapping: "Ledger Mapping",
  mappingHelp:
    "Tally never creates a ledger or cost centre it does not have, so every account, supplier, customer and Dimension value a voucher names needs its name in Tally. Attach your Tally masters export for LAMP to propose them, or map one by hand.",
  mappingAttach: "Tally masters export (XML)",
  mappingProposalHeading: "What LAMP proposes",
  mappingProposalNote:
    "Nothing is saved until you confirm. Untick what is wrong; what LAMP offers to add is added only if you tick it.",
  mappingNothingNew: "This export proposes nothing new.",
  mappingBy: (by: string): string => (by === "gstin" ? "by GSTIN" : by === "name" ? "by name" : "by group and name"),
  mappingOfferAccounts: "Add these Tally expense ledgers as expense accounts of your own",
  mappingOfferValues: "Add these cost centres as values of",
  mappingUnmatchedHeading: "Not matched: map these by hand, or leave them",
  mappingCandidates: "Fits:",
  mappingConfirm: "Confirm",
  mappingConfirmed: (mapped: number, accounts: number, values: number) =>
    `${mapped} ${mapped === 1 ? "mapping" : "mappings"} saved; ${accounts} expense ${accounts === 1 ? "account" : "accounts"} and ${values} ${values === 1 ? "value" : "values"} added.`,
  mappingAccounts: "Accounts",
  mappingParties: "Suppliers and customers",
  mappingValues: "Dimension values",
  mappingNothing: "Nothing here yet.",
  mappingNotMapped: "Not mapped",
  mappingInTally: "In Tally:",
  mappingMap: (lamp: string) => `Map to Tally: ${lamp}`,
  mappingMapButton: "Map",
  mappingChangeButton: "Change",
  mappingSearch: (lamp: string) => `Tally name for ${lamp}`,
  mappingTallyNameField: "Name in Tally",
  mappingFound: "Tally's names from your last masters export",
  mappingSaved: (lamp: string, name: string) => `“${lamp}” is mapped to “${name}”.`,

  // The Ring the business is on (M3-20b), in Settings, and what changed in LAMP, on the Quality page
  ringHeading: "New LAMP releases",
  ringAlpha: "Get new LAMP releases early (alpha)",
  ringAlphaHelp:
    "Your business moves to each new release just after LAMP's own, before most others. You can opt out at any time.",
  ringInternal: "Your business is on LAMP's internal ring: new releases reach it first.",
  ringSaved: (alpha: boolean): string =>
    alpha
      ? "Done: new releases will reach your business early."
      : "Done: new releases will reach your business with most others.",
  ringName: (ring: "internal" | "alpha" | "general"): string =>
    ring === "internal" ? "LAMP's internal ring" : ring === "alpha" ? "the alpha ring" : "the general ring",
  qualityChangesHeading: "What changed in LAMP",
  qualityOnRelease: (release: string, ring: string) => `Your business is on release ${release}, on ${ring}.`,
  qualityNoChanges: "LAMP has not changed for your business yet.",
  qualityRelease: (release: string, at: string) => `Release ${release}, ${at}`,
  qualityRolledBack: (previous: string) => `Moved back: what changed in release ${previous} no longer applies.`,
  qualityNothingForYou: "Nothing changed in the work your business does.",
  qualityOwnersOnly: "Only an Owner sees what changed in LAMP.",
  qualityLoading: "Loading what changed…",
  ladderHeading: "How much LAMP does alone",
  ladderLoading: "Loading how much LAMP does alone…",
  ladderIntro:
    "For each kind of work: the level it runs at, the highest it has earned in LAMP's tests, and what happened to its work here. Lower it whenever you like; raise it only as far as it has earned.",
  ladderReadOnly: "Only an Owner moves work up or down this ladder.",
  ladderNothing: "No work LAMP does alone in the Value Streams you can see yet.",
  ladderRungs: "Manual, then Ask, then Auto",
  /** A kind of work in the agents module's words; its Task Class when it has none. */
  ladderName: (name: string, taskClass: string) => {
    const said = name === "" ? taskClass : name;
    return said.charAt(0).toUpperCase() + said.slice(1);
  },
  autonomyLevel: (level: "manual" | "ask" | "auto"): string =>
    level === "manual" ? "Manual" : level === "ask" ? "Ask" : "Auto",
  ladderEarned: (level: string, on: string) => `Highest earned in LAMP's tests: ${level} (tested ${on}).`,
  ladderNotTested: "Not yet earned in LAMP's tests: Manual until it is.",
  ladderRunsAt: (level: string) => `runs at ${level}`,
  ladderCap: (cap: "not_earned" | "paused" | "track_record" | "set_by_you"): string =>
    cap === "not_earned"
      ? "not yet earned in LAMP's tests"
      : cap === "paused"
        ? "paused by LAMP"
        : cap === "track_record"
          ? "lowered by its record here"
          : "set by you",
  ladderLowered: (level: string, reason: string) => `LAMP lowered it to ${level}: ${reason}.`,
  ladderRaiseBackAlso: "Also move its Tasks up the ladder",
  ladderRaiseBack: (level: string) => `Raise it back to ${level}`,
  ladderNotNow: "Not now",
  ladderRaisedBack: (level: string) => `Raised back: it may now run at up to ${level}.`,
  ladderNotSuggested: "Dismissed: LAMP will not suggest it again for 30 days.",
  ladderCounts: (days: number, drafted: number, unchanged: number, changed: number, rejected: number, later: number) =>
    `Last ${days} days: drafted ${drafted}, you approved ${unchanged} unchanged, changed ${changed}, rejected ${rejected}; ${later} corrected later.`,
  ladderLower: (level: string) => `Lower to ${level}`,
  ladderRaise: (level: string) => `Raise to ${level}`,

  // A Tree Change's preview
  /** A confirmed change: what now happens, never a version number (#784). */
  changeApplied: "Changed: from now on your business works as the change says.",
  changeMoved: (moved: number, stayed: number) =>
    `Moved ${openRuns(moved)} to it; ${stayed} ${stayed === 1 ? "stays on its" : "stay on their"} version.`,
  changePreviewLabel: "Preview of the change",
  changeKeepVersion: "Open Runs keep the version they started on unless you move them.",
  changeAlsoMove: (n: number) => `Also move the ${openRuns(n)} whose step still exists to the new version`,
  changeConfirm: "Confirm the change",
  changeNotNow: "Not now",
  changeAppliedStatus: "Applied: your business works this way now.",
  changeSentToOwner: "Sent to the Owner as a Suggestion. It applies once an Owner confirms it.",
  changeReplayLabel: "Tested against your recent Runs",
  changeBlocked: "This change cannot be confirmed:",
  changeAgentTestLabel: "Test with the agent",
  changeAgentTestOffer: (runs: number) =>
    `${runs === 1 ? "1 recent Run was" : `${runs} recent Runs were`} not tested, because the agent's instructions changed. The agent can answer again on what it read then, with the new instructions.`,
  changeAgentTest: (credits: string) => `Test with the agent (≈ ${credits} Credits)`,
  changeAgentSkip: "Skip",
  changeAgentSkipped: "Not tested with the agent: those Runs stay untested. You can still confirm the change.",
  changeAgentTestedLabel: "The agent's new answers",

  // Settings → Privacy
  privacyUnreachable: "LAMP could not be reached. Check your connection and try again.",
  privacyLoading: "Loading Privacy…",
  privacyStatusAwaitingCode: "Waiting for your code",
  privacyStatusVerified: "Confirmed",
  privacyStatusForwarded: "With whoever decides it",
  privacyStatusCompleted: "Done",
  privacyStatusRefused: "Refused, with the reason",
  privacyPassedToBusiness: (reference: string, date: string) =>
    `Request ${reference} was passed to the business on ${date}. The business decides what happens to its data and answers you.`,
  privacyConfirmedForBusiness: (reference: string, date: string) =>
    `Request ${reference} is confirmed. LAMP passes it to the business by ${date}.`,
  privacyWithGrievanceOfficer: (reference: string, date: string) =>
    `Request ${reference} is with LAMP's Grievance Officer, who completes it by ${date}.`,
  privacyConfirmedForLamp: (reference: string, date: string) =>
    `Request ${reference} is confirmed. It reaches LAMP's Grievance Officer shortly, who completes it by ${date}.`,
  privacyPurposeAccount: "Running your LAMP account: sign-in, the businesses you belong to and service messages",
  privacyPurposeSensitiveData: "Your PIN and, for the Account Holder, payment details (sensitive personal data)",
  privacyPurposeQualityMonitoring: "Masked quality checks and test cases made from Runs you took part in",
  privacyPurposeProductNews: "Product news (optional)",
  privacyRightAccess: "See the data LAMP holds about me",
  privacyRightCorrection: "Correct or update my data",
  privacyRightErasure: "Delete my data",
  privacyRightWithdrawConsent: "Withdraw my consent",
  privacyRightNominate: "Name someone to act for me",
  privacyRightGrievance: "Make a complaint",
  privacyNoticeVersion: (version: string) => `Privacy notice version ${version}`,
  privacyWhatFor: "What for",
  privacyYourAnswer: "Your answer",
  privacyGiven: "Given",
  privacyWithdrawn: "Withdrawn",
  privacyNotAsked: "Not asked yet",
  privacyConsentDetail: (date: string, notice: string, channel: string) => `${date}, notice ${notice}, ${channel}`,
  privacyNoticeChanged: "The notice changed: please answer again",
  privacyWhatItStops: "What this stops",
  privacyStopsSensitiveData:
    "LAMP deletes your PIN at once, so signing in and confirming a sensitive action will need a fresh code. If you are the Account Holder and pay for a monthly plan, LAMP also cancels its saved UPI Autopay or card mandate, so nothing more is collected: the period already paid for runs to its end, and to keep LAMP after it you choose the plan again in Plan and billing, which asks for your payment details again.",
  privacyStopsQualityMonitoring:
    "From now on, Runs you take part in are left out of LAMP's quality checks and out of test cases made from real Runs. What was already checked is not pulled back.",
  privacyStopsProductNews:
    "LAMP sends you no product news, in any Channel. Service messages, such as sign-in codes, Tasks, approvals and invoices, still come.",
  privacyStopsAccount:
    "LAMP cannot keep your account open without this consent, because it cannot sign you in without it. Instead, ask below for your account to be deleted (an erasure request about your LAMP account).",
  privacyStopsAccountHolder:
    "If you are the Account Holder, you can offboard the whole business instead, under Leave LAMP below.",
  privacyConfirmWithdraw: "Withdraw consent",
  privacyKeepConsent: "Keep it",
  privacyGiveConsent: "Give consent",
  privacyWithdraw: "Withdraw",
  privacyTooManyRequests: (time: string) =>
    `Too many requests have been made, so no code was sent. Try again after ${time}.`,
  privacyCodeFailed: "That code did not work. Check it, or make the request again for a new code.",
  privacyConfirmRequest: "Confirm your request",
  privacyRequestReceived: (reference: string, date: string, contact: string) =>
    `LAMP has your request ${reference} and will complete it by ${date}. If ${contact} is registered with LAMP, a code is on its way there: enter it to confirm it is you.`,
  privacyCode: "Code",
  privacyConfirm: "Confirm",
  privacyMakeRequest: "Make a request",
  privacyWhatYouWant: "What you want",
  privacyWhatAbout: "What it is about",
  privacyAboutAccount: "My LAMP account: my name, sign-in details and the businesses I belong to. LAMP decides.",
  privacyAboutBusiness:
    "Data a business keeps in LAMP, such as its customer or supplier records. That business decides; LAMP passes your request to it within 72 hours.",
  privacyYourRegistered: "Your registered",
  privacyEmail: "Email",
  privacyMobile: "Mobile number",
  privacyRegisteredEmail: "Registered email",
  privacyRegisteredMobile: "Registered mobile number",
  privacyTellMore: "Tell us more",
  privacySendRequest: "Send request",
  privacyNoRequests: "You have made no requests.",
  privacyDoneOn: (date: string) => `on ${date}`,
  privacyDue: (date: string) => `due ${date}`,
  privacyBusinessRequestsHeading: "Requests about your business's data",
  privacyBusinessRequestsIntro:
    "People ask LAMP about data your business keeps; LAMP passes each request to you. Your business decides and answers them. GST records must be kept, so say so when a request cannot be met yet.",
  privacyNoneWaiting: "No requests are waiting.",
  privacyOverdue: (n: number) => `${n === 1 ? "1 request is" : `${n} requests are`} past the 15 days promised.`,
  privacyWhatYouDid: "What you did",
  privacyMarkDone: "Mark done",
  privacyRefuse: "Refuse, with this reason",
  privacyReviewerHeading: "Who at LAMP looked at your data",
  privacyReviewerIntro:
    "LAMP checks its agents' work on masked copies. A reviewer sees unmasked data only to fix a specific problem, and says why. Every look is listed here.",
  privacyNobodyLooked: "Nobody at LAMP has looked at your business's data.",
  privacyWhen: "When",
  privacyWho: "Who",
  privacyWhat: "What",
  privacyMasked: "Masked",
  privacyWhy: "Why",
  privacyUnmasked: "Unmasked",
  privacyTestCase: (id: string, shared: boolean) =>
    `test case ${id} (${shared ? "shared, masked" : "kept for your business"})`,
  privacyExportHeading: "Your business's data",
  privacyExportIntro:
    "Download everything the business keeps in LAMP: its Documents, Ledgers, records and a data dictionary, as CSV and JSON files. LAMP asks you to confirm it is you first.",
  privacyExported: (file: string) => `Downloaded ${file}.`,
  privacyStartOffboardingWhat:
    "You are about to start offboarding the business. It then has 30 days to download its data.",
  privacyShredWhat: "You are about to crypto-shred the business's bank details and ID numbers. This cannot be undone.",
  privacyLeaveHeading: "Leave LAMP",
  privacyOnlyHolder: "Only the Account Holder offboards the business.",
  privacyShredded: "This business was offboarded. Its bank details and ID numbers were crypto-shredded.",
  privacyOffboardIntro:
    "When the business leaves LAMP, it has 30 days to download everything: its Documents, Ledgers, records and a data dictionary, as CSV and JSON files any spreadsheet or accounting system can read.",
  privacyStartOffboarding: "Start offboarding",
  privacyDownloadBy: (date: string) =>
    `Download the export by ${date}, with "Download all of the business's data" under "Your business's data" above.`,
  privacyEndOf30Days: "the end of the 30 days",
  privacyDownloadAll: "Download all of the business's data",
  privacyConfirmEach: "Before LAMP crypto-shreds the business, confirm each of these. LAMP keeps a record of each.",
  privacyArchive: "An archive (optional)",
  privacyShredNow: "Crypto-shred the business now",
  privacyStay: "Stay on LAMP",
  privacyConsentRecorded: "Thank you. LAMP recorded your consent with today's date and the notice version.",
  privacyConsentWithdrawn:
    "Withdrawn. LAMP has stopped what relied on it, from now, and does not undo what was done before. It still keeps security logs and billing records for as long as the law requires.",
  privacyIntro: "What LAMP does with your personal data is in its privacy notice. Who else processes it is on the",
  privacySubProcessorLink: "sub-processor list",
  privacyIntroEnd: ".",
  privacyConsentHeading: "Your consent",
  privacyRightsHeading: "Your rights",
  privacyRightsIntro:
    "See, correct or delete your data, withdraw consent, name someone to act for you, or complain. LAMP acknowledges at once and completes it within 15 days. There is no fee.",
  privacyRequestsHeading: "Your requests",

  // The public signup page
  signupTitle: "Start your 30-day LAMP trial",
  signupIntro: "No card needed. We email you a code to confirm it is you.",
  signupBusiness: "Business name",
  signupName: "Your name",
  signupEmail: "Email",
  signupConsent: "I agree to LAMP using my details as its privacy notice says.",
  signupPrivacyLink: "How LAMP keeps your data safe",
  signupSubmit: "Email me a code",
  signupSent: (email: string) => `We sent a code to ${email}. It works for 10 minutes.`,
  signupCode: "Code",
  signupTrust: "Keep me signed in on this device",
  signupVerify: "Confirm and start",
  signupResend: "Send a new code",
  signupResent: "A new code is on its way.",
  signupTooMany: "Too many signups from here just now. Try again in an hour.",
  signupCheck: "Check your details, then try again.",
  signupBadCode: "That code didn't work. Check it, or ask for a new one.",
  signupFailed: "Something went wrong on our side. Try again in a few minutes.",
  signupNotServed: "Not a manufacturer that makes to order?",
  signupWaitlistLink: "Join the waitlist",
  signupHaveAccount: "Already use LAMP?",
  signupSignInLink: "Sign in",

  // The public waitlist page
  waitlistTitle: "Join the LAMP waitlist",
  waitlistIntro:
    "LAMP serves make-to-order manufacturers today. Tell us about your business and we will tell you when LAMP can serve it.",
  waitlistName: "Your name",
  waitlistBusiness: "Business name",
  waitlistContact: "Mobile number or email",
  waitlistCity: "City",
  waitlistIndustry: "What your business does",
  waitlistIndustryPick: "Choose one",
  waitlistIndustries: [
    { id: "machining", label: "Machining parts to customer drawings" },
    { id: "fabrication", label: "Sheet-metal or structural fabrication to order" },
    { id: "assembly", label: "Assembling machines or panels to order" },
    { id: "moulding", label: "Moulding to order" },
    { id: "casting", label: "Casting or forging to order" },
    { id: "trading", label: "Trading or distribution" },
    { id: "retail", label: "A shop" },
    { id: "food", label: "Restaurant, bakery or food" },
    { id: "services", label: "Services" },
    { id: "other", label: "Something else" },
  ] as readonly { readonly id: string; readonly label: string }[],
  waitlistWants: "What do you want LAMP to do for you?",
  waitlistPricesHeading: "What would you pay for LAMP, in ₹ a month?",
  waitlistTooCheap: "So cheap you would doubt it works",
  waitlistCheap: "A bargain",
  waitlistExpensive: "Getting expensive, but still worth considering",
  waitlistTooExpensive: "Too expensive to consider",
  waitlistConsent: "I agree to LAMP keeping these answers and contacting me about LAMP, as its privacy notice says.",
  waitlistSubmit: "Join the waitlist",
  waitlistJoined: "Thank you. You are on the waitlist, and we will tell you when LAMP can serve you.",
  waitlistCheck: "Check your answers, then try again.",
  waitlistPricesOrder: "The four prices go up in order: too cheap, a bargain, expensive, too expensive.",
  waitlistTooMany: "Too many entries from here just now. Try again later.",

  // The public sub-processor list
  subProcessorsTitle: "Who processes personal data for LAMP",
  subProcessorsIntro:
    'These third parties process personal data for LAMP\'s hosted service. LAMP tells Account Holders at least 30 days before it adds one or changes what one receives or where. Entries marked "being confirmed" are planned but their contract, region or terms are still being checked.',
  subProcessorsProvider: "Provider",
  subProcessorsWhatTheyDo: "What they do",
  subProcessorsWhatTheyReceive: "What they receive",
  subProcessorsWhere: "Where",
  subProcessorsBusinessData: "Business data",
  subProcessorsBeingConfirmed: "being confirmed",
  subProcessorsTenantDataYes: "yes",
  subProcessorsTenantDataWhenEnabled: "when enabled",
  subProcessorsTenantDataLampOnly: "LAMP only",
  subProcessorsNotListed:
    "Not on this list: assistants you connect to LAMP yourself, government portals that receive the business's filings, and accounting software the business exports to. They receive data only when you or the business choose.",
  subProcessorsBack: "Back to LAMP",
  subProcessorsGroupHosting: "Hosting and infrastructure",
  subProcessorsGroupModels: "AI models",
  subProcessorsGroupMessaging: "Messaging Channels",
  subProcessorsGroupEmail: "Email and SMS",
  subProcessorsGroupPayments: "Payments",
  subProcessorsAwsPurpose: "Hosts LAMP: servers, database, file storage, backups and logs",
  subProcessorsAwsReceives: "All data held in LAMP, encrypted at rest",
  subProcessorsAwsCountry: "India (Mumbai)",
  subProcessorsAnthropicPurpose: "Reads documents and drafts for LAMP's agents on hard tasks",
  subProcessorsAnthropicReceives: "Only the text a task needs, with names and numbers masked where the task allows",
  subProcessorsOpenRouterProvider: "OpenRouter, Inc., and the model hosts it routes to",
  subProcessorsOpenRouterPurpose: "Routes routine tasks to smaller models",
  subProcessorsAlternativePurpose: "An alternative model, if a task qualifies on it",
  subProcessorsMaskedReceives: "Only the text a task needs, masked where the task allows",
  subProcessorsUnitedStates: "United States",
  subProcessorsUsEu: "United States, European Union",
  subProcessorsTelegramPurpose: "The Telegram Channel and sign-in codes",
  subProcessorsTelegramReceives: "Messages with LAMP's bot, Telegram ID, name and verified mobile number",
  subProcessorsOutsideIndia: "Outside India",
  subProcessorsWhatsAppPurpose: "The WhatsApp Channel, once enabled",
  subProcessorsWhatsAppReceives: "Messages, files, WhatsApp numbers and profile names. Never used for shared learning",
  subProcessorsWhatsAppCountry: "United States and other Meta data centres",
  subProcessorsSesPurpose: "Email sign-in codes, invitations and notices; Documents the business emails",
  subProcessorsSesReceives: "Email address, message and attachments",
  subProcessorsIndia: "India",
  subProcessorsSmsProvider: "An SMS provider, if switched on",
  subProcessorsSmsPurpose: "SMS sign-in codes",
  subProcessorsSmsReceives: "Mobile number and the code",
  subProcessorsRazorpayPurpose: "Collects LAMP's subscription fees",
  subProcessorsRazorpayReceives: "The Account Holder's billing details; payment details entered on Razorpay's own page",

  // The public trust page
  trustTitle: "How LAMP works and keeps your data safe",
  trustOnThisPage: "On this page",
  // The legal notices (M3-29c)
  legalPrivacyNotice: "Privacy notice",
  legalContactAndGrievances: "Contact and grievances",
  legalVersion: (version: string) => `Version ${version}`,
  legalReadInEnglish: "Read the English version",
  legalDraftBody: "This notice has not been reviewed by a lawyer yet. It is shown only on a development Cell.",
  legalUnavailableTitle: "This notice is not published yet",
  legalUnavailable: "LAMP publishes its legal notices once a lawyer has reviewed them.",
  trustDraft: "DRAFT.",
  trustDraftBody: "Parts of this page have not been reviewed by a lawyer yet. It is shown only on a development Cell.",
  trustSectionDraft: "draft",
  trustUnpublishedTitle: "LAMP trust page",
  trustUnpublished: "This page is not published yet.",
  trustBack: "Back to LAMP",
  trustRecoveryHeading: "Recovery, as our latest restore drill achieved it",
  trustRecoveryDrill: (date: string, tested: string) => `In the restore drill of ${date}, which tested ${tested}:`,
  trustRecoveryTime: "LAMP was back in (recovery time)",
  trustRecoveryPoint: "Data lost, at most (recovery point)",
  trustDuration: (hours: number, minutes: number) =>
    hours
      ? minutes
        ? `${count(hours, "hour")} ${count(minutes, "minute")}`
        : count(hours, "hour")
      : count(minutes, "minute"),
  trustNoReport: "No report has been published yet.",
  trustReportPeriod: (from: string, to: string) => `${from} to ${to}`,
  trustReportFor: (period: string) => `Report for ${period}`,
  trustReportGenerated: (date: string, publishedBy: string) =>
    `Both days included (UTC). Generated on ${date}. Published by ${publishedBy}.`,
  trustReportBusinesses: (tenants: string) => `Businesses on LAMP during the period: ${tenants}.`,
  trustReportSource: (source: string) => `Source: ${source}`,
  trustReportFigure: "Figure",
  trustReportForPeriod: "For the period",
  trustReportNoChanges: "No changes in the period.",
  trustRecentIncidents: "Recent incidents",
  trustPostmortem: "What happened and what we changed",
  // The Cockpit's Audit and Credits views
  viewAuditLog: "Audit Log",
  viewFilterRun: "Run",
  viewFilterObject: "Business Object",
  viewFilterObjectId: "Business Object id",
  viewFilterActor: "Person (or system)",
  viewFilterAgent: "Agent task class",
  viewFilterFrom: "From",
  viewFilterTo: "To",
  viewFilterAfter: "After entry",
  viewFilter: "Filter",
  viewNoEntries: "No entries match.",
  viewWhen: "When",
  viewAction: "Action",
  viewRun: "Run",
  viewNode: "Node",
  viewBusinessObject: "Business Object",
  viewWho: "Who",
  viewAgent: "Agent",
  viewSystemActor: "System Actor",
  viewCredits: "Credits",
  viewCreditsAmount: (credits: string) => `${credits} Credits`,
  /** `run` and `credits` are HTML: the Run's id in `<code>` and the amount in `<strong>`. */
  viewCreditsUsed: (run: string, credits: string, steps: number, modelCalls: number) =>
    `Run ${run} has used ${credits}: ${count(steps, "step")}, ${count(modelCalls, "model call")}.`,
  viewStepOrTask: "Step or Task",
  viewRunInAudit: "This Run in the Audit Log",
  viewSignIn: "Sign in",
  viewSignInBody: "Sign in to web chat to see the Cockpit.",
  viewNotAllowed: "Not allowed",
  viewNotAllowedBody: "Your Roles do not let you see this.",
  viewUnavailable: "Unavailable",
  viewUnavailableBody: "The Application API did not answer. Try again.",
  // Cash and ledgers (#486).
  cashHeading: "Where your money is",
  cashIntro: (asOf: string) => `As on ${asOf}. This page only shows: to record a payment or a bill, ask LAMP in Chat.`,
  cashLoading: "Loading Cash and ledgers…",
  cashLoadFailed: "Cash and ledgers could not be loaded. Try again shortly.",
  cashRefused:
    "Your Roles don't let you see bank and cash balances, so this page is not shown to you. An Owner can give your Role bank and cash balances in Roles and permissions.",
  cashEmptyTitle: "Nothing is recorded yet",
  cashEmptyIntro: "This page fills as your business records its money in LAMP. To start, record any of these in Chat:",
  cashEmptyInvoice: "an invoice to a customer, for what customers owe you",
  cashEmptyBill: "a supplier's bill, for what you owe your suppliers",
  cashEmptyPayment: "a payment received or made, for the money in your bank and in hand",
  cashBank: "Money in the bank",
  cashBankUnknown: "Not known",
  cashBankNotGiven: (moved: string, since: string | null) =>
    since === null
      ? "Opening balance not given."
      : `${moved} moved through LAMP since ${since}, opening balance not given.`,
  cashBankFrom: (opening: string, asOf: string | null, fromTally: boolean) =>
    `From the balance of ${opening}${asOf === null ? "" : ` at the end of ${asOf}`}${fromTally ? ", in your Tally masters" : ", as given"}, and what LAMP recorded after it.`,
  cashGiveOpening: "Give the bank's opening balance",
  cashGiveOpeningIntro:
    "The bank's balance in your books at the end of a day: LAMP adds what it records for the days after it. Write an overdraft with a minus sign.",
  cashGiveOpeningAmount: "Opening balance (₹)",
  cashGiveOpeningDate: "At the end of",
  cashForecastNotGiven: (since: string | null) =>
    since === null
      ? "The forecast opens on the money in the bank: give the bank's opening balance to see it."
      : `The forecast opens on the money in the bank, which LAMP knows only since ${since}: give the bank's opening balance to see it.`,
  cashGiveOpeningSave: "Save",
  cashGiveOpeningInvalid: "Give the opening balance in rupees and paise, and the date it is at.",
  cashGiveOpeningFailed: (problems: readonly string[]) =>
    problems.length > 0 ? problems.join(" ") : "The opening balance could not be saved. Try again shortly.",
  cashInHand: "Cash in hand",
  cashOwedToYou: "Customers owe you",
  cashYouOwe: "You owe suppliers",
  cashReceivables: "What customers owe you",
  cashPayables: "What you owe your suppliers",
  cashAgeing: "By how late it is",
  cashBucket: (bucket: string) =>
    ({
      not_due: "Not yet due",
      "1_30": "1–30 days late",
      "31_60": "31–60 days late",
      "61_90": "61–90 days late",
      over_90: "Over 90 days late",
    })[bucket] ?? bucket,
  cashNothingOwedToYou: "No customer owes you anything today.",
  cashNothingYouOwe: "You owe your suppliers nothing today.",
  cashInvoice: "Invoice",
  cashBill: "Bill",
  cashCustomer: "Customer",
  cashSupplier: "Supplier",
  cashDue: "Due",
  cashDaysLate: "Days late",
  cashOwedToYouColumn: "Owed to you",
  cashYouOweColumn: "You owe",
  cashForecastTitle: "Cash forecast, next 13 weeks",
  cashForecastIntro:
    "What LAMP expects in and out each week from open invoices, bills, payroll and statutory dues, starting from the money you hold today.",
  cashForecastNegative: (week: number) => `Week ${String(week)} is the first in which your money goes below nil.`,
  cashWeek: "Week",
  cashWeekSpan: (from: string, to: string) => `${from} – ${to}`,
  cashIn: "Money in",
  cashOut: "Money out",
  cashClosing: "Money left",
  cashBelowNil: "Below nil",
  sensitiveClass: (dataClass: string) =>
    ({
      bank_and_cash: "bank and cash balances",
      payroll: "payroll",
      costs_and_margins: "costs and margins",
      customer_pricing: "customer prices",
    })[dataClass] ?? dataClass,
  cashHidden: (classes: readonly string[]) =>
    `Your Roles don't show this part: it also needs ${
      classes.length > 1 ? `${classes.slice(0, -1).join(", ")} and ${classes.at(-1) ?? ""}` : (classes[0] ?? "")
    }.`,
  cashCouldNotRead: (problems: readonly string[]) => `LAMP could not read this part: ${problems.join("; ")}.`,
  cashLedgersTitle: "Ledgers",
  cashStock: "Stock",
  cashStockHow:
    "on hand today, each Item at its weighted average purchase cost: what a closing stock journal would post.",
  cashStockUnvalued: (items: number) =>
    `${String(items)} ${items === 1 ? "Item is" : "Items are"} held with no purchase or opening cost, so not valued.`,
  cashLedgersIntro:
    "Every account your Documents have posted to, with its balance and newest entries. Open an entry's Run, or an invoice's PDF, from its Document.",
  cashAccountName: (account: string, name: string) =>
    ({
      bank: "Bank",
      cash: "Cash in hand",
      receivable: "Customers (what they owe you)",
      payable: "Suppliers (what you owe them)",
      sales: "Sales",
      purchases: "Purchases",
      output_cgst: "Output CGST",
      output_sgst: "Output SGST",
      output_igst: "Output IGST",
      input_cgst: "Input CGST",
      input_sgst: "Input SGST",
      input_igst: "Input IGST",
      round_off: "Round-off",
      opening_balance_equity: "Opening balances brought in",
    })[account] ?? name,
  cashDebitBalance: (amount: string) => `${amount} debit`,
  cashCreditBalance: (amount: string) => `${amount} credit`,
  cashDate: "Date",
  cashDocument: "Document",
  cashCounterparty: "Customer or supplier",
  cashDebit: "Debit",
  cashCredit: "Credit",
  cashDocumentName: (object: string, number: string | null) => {
    const kind =
      {
        invoice: "Tax invoice",
        payment_receipt: "Payment received",
        supplier_bill: "Supplier bill",
        supplier_payment: "Payment to a supplier",
        credit_note: "Credit note",
        customer_debit_note: "Debit note",
        goods_receipt: "Goods received",
        stock_receipt: "Stock received",
        stock_issue: "Stock issued",
        opening_balance: "Opening balance",
        payroll_run: "Payroll",
        wage_payment: "Wages paid",
        statutory_payment: "Statutory payment",
        journal: "Journal",
      }[object] ?? object.replaceAll("_", " ");
    return number ? `${kind} ${number}` : kind;
  },
  cashShowing: (shown: number, all: number) => `The newest ${String(shown)} of ${String(all)} entries.`,

  // GST and compliance (#486).
  gstHeading: "GST and what falls due",
  gstIntro: "This page only shows: to prepare a return or record a filing, ask LAMP in Chat.",
  gstLoading: "Loading GST and compliance…",
  gstLoadFailed: "GST and compliance could not be loaded. Try again shortly.",
  gstRefused:
    "Your Roles don't let you see bank and cash balances and costs, which the GST position is read from, so this page is not shown to you. An Owner can change your Role in Roles and permissions.",
  gstPeriodLoading: (month: string) => `Loading ${month}…`,
  gstPeriodFailed: (month: string) => `${month} could not be read. Try again shortly, or choose another month.`,
  gstPeriod: "Period",
  gstPositionTitle: (period: string) => `GST for ${period}`,
  gstOutput: "GST on your sales",
  gstItc: "Input tax credit",
  gstNet: "Net GST to pay",
  gstCarried: "Credit carried forward",
  gstTax: "Tax",
  gstTotal: "Total",
  gstOutputRow: "On sales and reverse charge",
  gstItcRow: "Input tax credit",
  gstPayRow: "To pay in cash",
  gstNothingRecorded: (period: string) =>
    `Nothing taxable is recorded for ${period}. Issue your tax invoices and book your suppliers' bills in LAMP, and their GST shows here.`,
  gstNotLaidOut: "LAMP has not laid out this period's GSTR-3B yet: these are the figures its draft would have today.",
  gstNoVersionYet:
    "No GSTR-3B is prepared for this period yet: these are the figures its draft would have today. Ask LAMP in Chat to prepare it.",
  gstKeptVersion: (version: number, on: string) =>
    `These are the figures of GSTR-3B version ${String(version)}, prepared on ${on}.`,
  gstKeptChanged: (version: number, filed: boolean) =>
    filed
      ? `This period's postings or its GSTR-2B have changed since version ${String(version)} was prepared, so today's figures differ from the return filed. Ask your CA how to take the difference into a later return.`
      : `This period's postings or its GSTR-2B have changed since version ${String(version)} was prepared, so today's figures differ from these. Ask LAMP in Chat to prepare GSTR-3B again before it is filed.`,
  gstExportsTitle: "Returns for your CA",
  gstExportsIntro:
    "The GSTR-1 file and the GSTR-3B draft prepared for each filing, every version kept, to give your CA.",
  gstNoExports: (period: string) =>
    `No GSTR-1 or GSTR-3B is laid out for ${period} yet. Once LAMP's Compliance Cycle lays them out, ask LAMP in Chat to prepare them.`,
  gstExportKind: (kind: string) =>
    ({
      gstr1: "GSTR-1",
      gstr3b: "GSTR-3B",
      tds_salary: "TDS return on salaries (24Q)",
      tds_non_salary: "TDS return on other payments (26Q)",
    })[kind] ?? kind,
  gstExportTitle: (what: string, period: string) => `${what} for ${period}`,
  gstNotPrepared: (what: string, period: string) =>
    `Not prepared yet. Ask LAMP in Chat: “prepare ${what} for ${period}”.`,
  gstExportHidden: (problems: readonly string[]) => `Your Roles don't reach this: ${problems.join("; ")}.`,
  gstVersion: (version: number, on: string) => `Version ${String(version)}, ${on}:`,
  gstFilePart: (part: string) =>
    ({
      file: "Download the file",
      draft: "Download the draft",
      reconciliation: "Download the GSTR-2B reconciliation",
      deductees: "Download the deductees sheet",
      salary: "Download the salary sheet",
      annual: "Download the annual sheet",
    })[part] ?? part,
  gstTdsTitle: "TDS return data for your CA",
  gstNoTds: "No TDS return is laid out for this period.",
  gstCalendarTitle: (month: string) => `Filings due in ${month}`,
  gstCalendarIntro: "Each statutory filing and payment that falls due, with its date; one left past its date is late.",
  gstCycleOff:
    "LAMP's Compliance Cycle is not switched on for your business, so LAMP does not lay these out or remind you of them.",
  gstNothingDue: (month: string) => `Nothing falls due in ${month}.`,
  gstFiling: "Filing",
  gstFor: "For",
  gstDueOn: "Due",
  gstStatus: "Status",
  gstStatusDue: "Due",
  gstStatusDone: (on: string) => `Filed on ${on}`,
  gstStatusBeforeLamp: "Filed before LAMP",
  gstStatusLate: (days: number | null) => (days ? `Late by ${String(days)} ${days === 1 ? "day" : "days"}` : "Late"),
  gstCouldNotRead: (problems: readonly string[]) => `LAMP could not read this part: ${problems.join("; ")}.`,
  viewNotFound: "Not found",
  viewNotFoundBody: "There is no such page.",

  // AI models (#488)
  modelsLoading: "Loading AI models…",
  modelsIntro:
    "LAMP uses AI models of three sizes, and none at all where a rule will do. Each job goes to the smallest model that does it well, so your Credits go further.",
  modelsWork: "Kind of work",
  modelsWhat: "What it does",
  modelsUsed: "Used this month",
  modelsWhere: "Where it runs",
  modelsTierName: (tier: string) =>
    ({ T0: "No model", T1: "Small model", T2: "Mid-size model", T3: "Largest model" })[tier] ?? tier,
  modelsTierWork: (tier: string) =>
    ({
      T0: "Routes, rules, postings to the ledgers, printing documents and sending set messages.",
      T1: "Understanding messages in English, Hindi or Hinglish, picking out details and writing chat replies.",
      T2: "Reading photos and PDFs of documents, drafting quotations and estimates, and judging what a Playbook says.",
      T3: "Drafting new ways of working and complex changes, and a second try when a mid-size model's answer fails its checks.",
    })[tier] ?? "",
  /** `shown` is `calls` as the page writes numbers. */
  modelsCalls: (calls: number, shown: string, credits: string) =>
    `${shown} ${calls === 1 ? "call" : "calls"}, ${credits} Credits`,
  modelsSteps: (steps: number, shown: string, credits: string) =>
    `${shown} ${steps === 1 ? "step" : "steps"}, ${credits} Credits`,
  modelsNoModel: "No model is called",
  modelsOutsideIndia: "Outside India",
  modelsInsideCell: "Inside your LAMP, in India",
  modelsBudget: "Model Budget",
  modelsBudgetUsed: (used: string, budget: string, percent: number) =>
    `${used} of ${budget} Credits used this month (${percent}%).`,
  modelsBudgetLeft: (left: string) => `${left} Credits left. Credits and usage has the details and top-ups.`,
  modelsData: "Your data and the models",
  modelsLeavesIndia:
    "Some model calls leave India: the AI models run at providers outside India. What a call sends goes only to providers held to these terms:",
  modelsStaysInIndia: "No model call leaves India: every AI model runs inside your LAMP.",
  modelsNoTraining: "They never train their models on your business's data.",
  modelsZeroRetention: "They keep nothing of it once they have answered.",
  modelsSensitive: "LAMP sends a model only what the job needs, and never more than the person it works for may see.",

  // Security (#488)
  securityLoading: "Loading Security…",
  securitySignIn: "How you sign in",
  securityMethod: (method: string) =>
    ({ email: "A code sent to your email", telegram: "A code sent to you on Telegram", sms: "A code sent by SMS" })[
      method
    ] ?? method,
  securityNoMethod: "No sign-in method is set up for you. Ask an Owner to invite you again.",
  securityPin: "A PIN, for when a code does not arrive",
  securityNoPin: "No PIN set.",
  securityPinNew: "A new PIN, six digits",
  securityPinSet: "Set my PIN",
  securityPinSaved: "Your PIN is set.",
  securityPinNotSet: "No PIN was set.",
  securityPinWeak: "That PIN is too easy to guess. Choose six digits that are not a pattern, and try again.",
  securityPinCancel: "Not now",
  securitySessions: "Where you are signed in",
  securitySessionsIntro: "Each time you signed in during the last 30 days, newest first.",
  securitySignedIn: "Signed in",
  securityDevice: "Device",
  securityThisDevice: "This device",
  securityOtherDevice: "Another device",
  securityKept: "Stays signed in",
  securityTrusted: "30 days",
  securityUntrusted: "12 hours",
  securityState: "Now",
  securityLive: "Signed in",
  securityEnded: (when: string) => `Signed out ${when}`,
  securityExpired: "Ended",
  securitySignOutEverywhere: "Sign out everywhere else",
  securitySignedOutEverywhere: (ended: number) =>
    ended === 0 ? "You were not signed in anywhere else." : `Signed out of ${count(ended, "other session")}.`,
  securityAssistants: "Assistants with access",
  securityBreakGlass: "LAMP support's access to your business",
  securityBreakGlassIntro:
    "LAMP's people see your business's data only through Break-glass Access: with an Owner's consent, or for a declared incident, for at most four hours, and every use is in the Audit Log.",
  securityBreakGlassNone: "LAMP support has not opened access to your business in the last 90 days.",
  securityBreakGlassWhen: "When",
  securityBreakGlassWhat: "What happened",
  securityBreakGlassWhy: "Why",
  securityBreakGlassBasis: (basis: string) =>
    ({ owner_consent: "With an Owner's consent", declared_incident: "For a declared incident" })[basis] ?? "",
  securityBreakGlassUntil: (until: string) => `Until ${until}`,

  // Help (#488)
  helpLoading: "Loading help…",
  helpSearch: "Search help",
  helpSearchHint: "Words from what you want to know, such as invoice or delivery",
  helpMatched: (shown: number, matched: number) =>
    shown < matched ? `${shown} of ${matched} topics. Add words to narrow them.` : count(matched, "topic"),
  helpNone: "No help topic has all those words. Try fewer words, or ask LAMP.",
  helpAsk: "Ask LAMP",
  helpSupport: "Reaching support",
  helpSupportAgent:
    'Say "help", or ask your question, in chat, on Telegram or on WhatsApp. LAMP\'s support agent answers at any hour, in English, Hindi or Hinglish, about what your Roles let you see.',
  helpSupportPeople:
    "When it cannot sort something out, or you have a complaint, a person at LAMP takes it over: Monday to Saturday, 10 am to 7 pm, in English or Hindi. They reply within one working day, on every plan.",
  helpGrievance: "A complaint about how your personal data is handled goes to LAMP's Grievance Officer:",
  helpGrievanceLink: "Contact and grievances",

  // System status (#488)
  statusLoading: "Checking LAMP…",
  statusAllWorking: "LAMP is working.",
  statusSomeSlow: "LAMP is working, but slowly in places.",
  statusSomeDown: "Part of LAMP is not working. LAMP's team is told at once.",
  statusPart: "Part",
  statusState: "Now",
  statusSince: "Since",
  statusPartName: (part: string) =>
    ({
      api: "The Cockpit and web chat",
      worker: "Background work: Runs, messages and documents",
      models: "AI models",
      backup: "Last backup",
    })[part] ?? part,
  statusChannelName: (channel: string) =>
    ({ web_chat: "Web chat", telegram: "Telegram", whatsapp: "WhatsApp", email: "Email" })[channel] ?? channel,
  statusChannels: "Channels",
  statusStateName: (state: string) => ({ working: "Working", slow: "Slow", down: "Down" })[state] ?? state,
  statusBackupAt: (when: string) => `Backed up ${when}`,
  statusNoBackup: "This LAMP keeps no backup report to show here.",
  statusCheckedAt: (when: string) => `Checked at ${when}.`,
  statusCheckAgain: "Check again",

  // Why a Run stops (#570), one sentence per stop code, as the api's catalogue says it.
  stopNoCommittedDate: ({ order }: StopParameters["no_committed_date"]) =>
    `Sales order ${order} has no delivery date, so its work orders can't be planned. ` +
    `Tell me the date promised to the customer: say “delivery date for ${order} is 24 October”, ` +
    `or give it on this card.`,
  stopNoBillOfMaterials: ({ order, items }: StopParameters["no_bill_of_materials"]) =>
    `${items.join(", ")} ${items.length === 1 ? "has" : "have"} no bill of materials yet, so the material for ` +
    `sales order ${order} can't be checked. Its work orders are planned and go on once the BOM is made.`,
  stopMaterialShort: ({ order, due, lines }: StopParameters["material_short"]) =>
    `Material is short for sales order ${order}, due ${dayIn("en-IN", due)}. ${lines.map(shortLine).join(". ")}. ` +
    "The work orders wait for it before they are released.",
  stopNoGstRate: ({ item, hsn, invoice, stale }: StopParameters["no_gst_rate"]) =>
    `No GST rate is on record for ${item} (HSN ${hsn}).` +
    `${stale === undefined ? "" : ` The ${stale}% on the Item is not a GST rate now.`} ` +
    `Give its rate to raise ${invoice ? `invoice ${invoice}` : "the invoice"}: type it on this card, such as 18%.`,
  stopAlreadyBilled: ({ order, invoices }: StopParameters["already_billed"]) =>
    `Sales order ${order} is already billed by invoice ${invoices.join(", ")}: no further invoice goes to the customer`,

  // LAMP's own tax invoices for payments to LAMP (#1011), for LAMP's own Owners
  statusLampInvoices: "LAMP's own tax invoices",
  statusLampInvoicesReady: "LAMP can issue its tax invoice for every payment made to it.",
  statusLampInvoicesNotReady:
    "LAMP cannot issue any tax invoice yet: give LAMP's own Supplier Particulars (name, address, GSTIN and " +
    "signatory) in Settings. Each payment waiting is invoiced on the next daily run after.",
  statusLampInvoicesNoneWaiting: "No payment is waiting for its tax invoice.",
  statusLampInvoicesTenant: "Business",
  statusLampInvoicesPayment: "Payment",
  statusLampInvoicesWhy: "Why it has no tax invoice",
  statusLampInvoiceReason: (reason: string, said: string) =>
    ({
      own_particulars_missing: "LAMP's own Supplier Particulars are not given.",
      particulars_missing: "The business has not chosen the Invoice Particulars to bill.",
      failed: "LAMP could not issue it this time; it is tried again.",
      waiting: "Not tried yet; the next daily run issues it.",
    })[reason] ?? said,
};

export type Words = typeof en;
